import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { format as formatDate, parseISO as parseIsoDate } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Trash2, Pencil, Layers, Tag, Contact, Briefcase, Copy, Package, Ticket, RefreshCw, CalendarDays, Link2, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";
import QuickSearchBar, { useQuickFilter } from "../components/QuickSearchBar";
import ExportCsvButton from "../components/ExportCsvButton";
import StickyPageHeader from "../components/StickyPageHeader";
import SearchableSelect from "../components/SearchableSelect";
import BeneficiaryCombobox from "../components/BeneficiaryCombobox";
import OffersEditor from "../components/OffersEditor";
import HorizontalScrollContainer from "../components/HorizontalScrollContainer";

const COLORS = ["#0055FF", "#059669", "#D97706", "#DC2626", "#7C3AED", "#0EA5E9", "#EC4899", "#71717A"];

/**
 * Cellule « Offres » réutilisée dans les 3 tableaux (Promo / Affiliation / Parrainage).
 * Affiche chaque offre du code sous forme de chip compact :
 *   `[Label · ]<valeur><unité> · <portée services>`
 *   - Portée : « Tous services » si aucune restriction, sinon « SVC1, SVC2 +N ».
 *   - Retourne « — » quand `offers` est vide (mono-offre legacy).
 * Le container reste borné (`max-w-[280px]`) pour ne pas casser la table sur mobile,
 * les chips wrappent naturellement (flex-wrap) et se tronquent au-delà.
 */
function OffersCell({ offers, accent }) {
  const list = Array.isArray(offers) ? offers : [];
  if (list.length === 0) {
    return <span className="text-xs text-zinc-400">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1 max-w-[280px]">
      {list.map((o, i) => {
        const isPct = o.discount_type === "percentage";
        const suffix = isPct ? "%" : "€";
        const val = Number(o.discount_value || 0).toLocaleString("fr-FR", {
          minimumFractionDigits: isPct ? 0 : 2,
          maximumFractionDigits: 2,
        });
        const names = Array.isArray(o.service_product_names) ? o.service_product_names.filter(Boolean) : [];
        const scope = names.length === 0
          ? "Tous services"
          : (names.length > 2 ? `${names.slice(0, 2).join(", ")} +${names.length - 2}` : names.join(", "));
        const label = (o.label || "").trim();
        const tooltip = `${label ? label + " · " : ""}${val} ${suffix} · ${names.length ? names.join(", ") : "Tous services"}`;
        return (
          <span
            key={o.id || `offer-${i}`}
            className="inline-flex items-center gap-1 text-[11px] text-zinc-700 border border-zinc-200 rounded-sm px-1.5 py-0.5 bg-zinc-50"
            title={tooltip}
          >
            <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: accent || "#7C3AED" }} />
            {label && (
              <span className="truncate max-w-[110px] text-zinc-600">{label}</span>
            )}
            <span className="font-mono font-semibold" style={{ color: accent || "#7C3AED" }}>{val} {suffix}</span>
            <span className="text-zinc-500 truncate max-w-[140px]">· {scope}</span>
          </span>
        );
      })}
    </div>
  );
}

/**
 * Sérialise `offers[]` pour l'export CSV (une ligne par offre séparée par « | »).
 */
function offersToCsv(offers) {
  const list = Array.isArray(offers) ? offers : [];
  if (list.length === 0) return "";
  return list.map((o) => {
    const isPct = o.discount_type === "percentage";
    const suffix = isPct ? "%" : "€";
    const val = Number(o.discount_value || 0).toLocaleString("fr-FR", {
      minimumFractionDigits: isPct ? 0 : 2,
      maximumFractionDigits: 2,
    });
    const names = Array.isArray(o.service_product_names) ? o.service_product_names.filter(Boolean) : [];
    const scope = names.length === 0 ? "Tous services" : names.join(", ");
    const label = (o.label || "").trim();
    return `${label ? label + " " : ""}${val} ${suffix} [${scope}]`;
  }).join(" | ");
}


function CategoryCard({ c, brand, brandsList, canManage, isAdmin, onEdit, onDelete, prefix }) {
  // Liste finale de départements (multi). Compat ascendante : si seul `brand` est passé (mode legacy),
  // on l'utilise ; sinon on hydrate depuis `brandsList` + `c.brand_ids`.
  const chips = (() => {
    if (brandsList && Array.isArray(c.brand_ids) && c.brand_ids.length > 0) {
      return c.brand_ids.map((id) => brandsList.find((b) => b.id === id)).filter(Boolean);
    }
    return brand ? [brand] : [];
  })();
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5 hover:border-zinc-300" data-testid={`${prefix}-${c.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="h-6 w-1 rounded-sm" style={{ backgroundColor: c.color }} />
          <Layers size={14} className="text-zinc-400" />
        </div>
        {canManage && (
          <div className="flex gap-1">
            {isAdmin && (
              <Button variant="ghost" size="sm" onClick={() => onEdit(c)} data-testid={`edit-${prefix}-${c.id}`}>
                <Pencil size={12} />
              </Button>
            )}
            {isAdmin && (
              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => onDelete(c.id)} data-testid={`delete-${prefix}-${c.id}`}>
                <Trash2 size={12} />
              </Button>
            )}
          </div>
        )}
      </div>
      <h3 className="font-display text-lg font-semibold text-zinc-900">{c.name}</h3>
      {c.description && <p className="text-sm text-zinc-600 mt-1">{c.description}</p>}
      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`${prefix}-brand-chips-${c.id}`}>
          {chips.map((b) => (
            <span key={b.id} className="inline-flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 bg-zinc-50 border border-zinc-200 rounded-sm text-zinc-700">
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: b.color }} />
              {b.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Categories() {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const isAdmin = user?.role === "admin";
  // RBAC iter111 : le Gestionnaire n'accède qu'aux 3 onglets « codes »
  // (Promo / Affiliation / Parrainage). Les 5 autres onglets sont masqués.
  const isGestionnaire = user?.role === "gestionnaire";
  const canSeeGeneralCats = !isGestionnaire;

  // Formatte une date ISO (YYYY-MM-DD) au format français JJ/MM/AAAA
  const formatDateFR = (iso) => {
    if (!iso) return "";
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
  };

  // Collab categories
  const [collabList, setCollabList] = useState([]);
  const [collabOpen, setCollabOpen] = useState(false);
  const [collabEditing, setCollabEditing] = useState(null);
  const [collabForm, setCollabForm] = useState({ name: "", description: "", color: "#0055FF", is_salarie: false, annual_leave_days: 25, is_leadership: false });

  // Shift categories
  const [shiftList, setShiftList] = useState([]);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [shiftEditing, setShiftEditing] = useState(null);
  const [shiftForm, setShiftForm] = useState({ name: "", description: "", color: "#0055FF", brand_ids: [] });

  // Beneficiary categories (Clientèle, Partenaire, Fournisseur…)
  const [benList, setBenList] = useState([]);
  const [benOpen, setBenOpen] = useState(false);
  const [benEditing, setBenEditing] = useState(null);
  const [benForm, setBenForm] = useState({ name: "", description: "", color: "#D97706" });

  // Intervention categories (utilisées par Agenda départements)
  const [intList, setIntList] = useState([]);
  const [intOpen, setIntOpen] = useState(false);
  const [intEditing, setIntEditing] = useState(null);
  const [intForm, setIntForm] = useState({ name: "", description: "", color: "#7C3AED", brand_ids: [] });
  const [intImporting, setIntImporting] = useState(false);

  // Services et produits (catalogue KPI)
  const emptyServiceForm = () => ({
    name: "",
    kind: "service",
    description: "",
    color: "#0EA5E9",
    brand_ids: [],
    price_ht: 0,
    vat_rate: 20,
    fees_ht: 0,
    disbursements: 0,
    tarif_sur_devis: false,
  });
  const [serviceList, setServiceList] = useState([]);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [serviceEditing, setServiceEditing] = useState(null);
  const [serviceForm, setServiceForm] = useState(emptyServiceForm());
  const [serviceFilters, setServiceFilters] = useState({ q: "", brand_id: "", kind: "" });

  // Codes promotionnels
  const emptyPromoForm = () => ({
    code: "",
    brand_ids: [],
    beneficiary_id: "",
    is_internal: false,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    is_indefinite: false,
    discount_type: "percentage",
    discount_value: 10,
    commission_enabled: false,
    commission_type: "percentage",
    commission_value: 0,
    // Commissions par service : { [service_id]: { commission_type, commission_value } }
    service_commissions: {},
    // Multi-offres de remise (List[{ id?, label, service_product_ids: [], discount_type, discount_value }])
    offers: [],
    manual_active: true,
    description: "",
  });
  const [promoList, setPromoList] = useState([]);
  const [promoContacts, setPromoContacts] = useState([]);  // contacts filtrés par brand du form
  const [promoServices, setPromoServices] = useState([]);  // services filtrés par brand du form
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoEditing, setPromoEditing] = useState(null);
  const [promoForm, setPromoForm] = useState(emptyPromoForm());
  const [promoFilters, setPromoFilters] = useState({ q: "", brand_id: "", status: "" });
  const [promoGenerating, setPromoGenerating] = useState(false);

  // Codes d'affiliation (miroir des codes promo)
  const emptyAffiliateForm = () => ({
    code: "",
    brand_ids: [],
    beneficiary_id: "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    is_indefinite: false,
    discount_type: "percentage",
    discount_value: 10,
    commission_enabled: false,
    commission_type: "percentage",
    commission_value: 0,
    service_commissions: {},
    offers: [],
    manual_active: true,
    description: "",
  });
  const [affiliateList, setAffiliateList] = useState([]);
  const [affiliateContacts, setAffiliateContacts] = useState([]);
  const [affiliateServices, setAffiliateServices] = useState([]);
  const [affiliateOpen, setAffiliateOpen] = useState(false);
  const [affiliateEditing, setAffiliateEditing] = useState(null);
  const [affiliateForm, setAffiliateForm] = useState(emptyAffiliateForm());
  const [affiliateFilters, setAffiliateFilters] = useState({ q: "", brand_id: "", status: "" });
  const [affiliateGenerating, setAffiliateGenerating] = useState(false);

  // Codes de parrainage (même pattern)
  const emptyReferralForm = () => ({
    code: "",
    brand_ids: [],
    contact_type: "beneficiary",
    beneficiary_id: "",
    collaborator_id: "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    is_indefinite: false,
    discount_type: "percentage",
    discount_value: 10,
    offers: [],
    manual_active: true,
    description: "",
  });
  const [referralList, setReferralList] = useState([]);
  const [referralContacts, setReferralContacts] = useState([]);
  const [referralCollabs, setReferralCollabs] = useState([]);
  const [referralServices, setReferralServices] = useState([]);
  const [referralOpen, setReferralOpen] = useState(false);
  const [referralEditing, setReferralEditing] = useState(null);
  const [referralForm, setReferralForm] = useState(emptyReferralForm());
  const [referralFilters, setReferralFilters] = useState({ q: "", brand_id: "", status: "" });
  const [referralGenerating, setReferralGenerating] = useState(false);

  // Filtres recherche rapide (un par onglet)
  const [collabFilters, setCollabFilters] = useState({ q: "" });
  const [shiftFilters, setShiftFilters] = useState({ q: "", brand_id: "" });
  const [benFilters, setBenFilters] = useState({ q: "" });
  const [intFilters, setIntFilters] = useState({ q: "", brand_id: "" });

  const filteredCollab = useQuickFilter(collabList, collabFilters, {
    text: (c) => `${c.name || ""} ${c.description || ""}`,
  });
  const filteredShift = useQuickFilter(shiftList, shiftFilters, {
    text: (c) => `${c.name || ""} ${c.description || ""}`,
    predicates: {
      brand_id: (c, value) => {
        const ids = c.brand_ids && c.brand_ids.length ? c.brand_ids : (c.brand_id ? [c.brand_id] : []);
        return ids.includes(value);
      },
    },
  });
  const filteredBen = useQuickFilter(benList, benFilters, {
    text: (c) => `${c.name || ""} ${c.description || ""}`,
  });
  const filteredInt = useQuickFilter(intList, intFilters, {
    text: (c) => `${c.name || ""} ${c.description || ""}`,
    predicates: {
      brand_id: (c, value) => {
        const ids = c.brand_ids && c.brand_ids.length ? c.brand_ids : (c.brand_id ? [c.brand_id] : []);
        return ids.includes(value);
      },
    },
  });
  const filteredServices = useQuickFilter(serviceList, serviceFilters, {
    text: (c) => `${c.name || ""} ${c.description || ""}`,
    predicates: {
      brand_id: (c, value) => (c.brand_ids || []).includes(value),
      kind: (c, value) => c.kind === value,
    },
  });
  const filteredPromos = useQuickFilter(promoList, promoFilters, {
    text: (p) => `${p.code || ""} ${p.beneficiary_name || ""} ${p.description || ""}`,
    predicates: {
      brand_id: (p, value) => {
        const ids = p.brand_ids && p.brand_ids.length ? p.brand_ids : (p.brand_id ? [p.brand_id] : []);
        return ids.includes(value);
      },
      status: (p, value) => (value === "active" ? p.is_active : !p.is_active),
    },
  });
  const filteredAffiliates = useQuickFilter(affiliateList, affiliateFilters, {
    text: (p) => `${p.code || ""} ${p.beneficiary_name || ""} ${p.description || ""}`,
    predicates: {
      brand_id: (p, value) => {
        const ids = p.brand_ids && p.brand_ids.length ? p.brand_ids : (p.brand_id ? [p.brand_id] : []);
        return ids.includes(value);
      },
      status: (p, value) => (value === "active" ? p.is_active : !p.is_active),
    },
  });
  const filteredReferrals = useQuickFilter(referralList, referralFilters, {
    text: (p) => `${p.code || ""} ${p.contact_name || p.beneficiary_name || ""} ${p.description || ""}`,
    predicates: {
      brand_id: (p, value) => {
        const ids = p.brand_ids && p.brand_ids.length ? p.brand_ids : (p.brand_id ? [p.brand_id] : []);
        return ids.includes(value);
      },
      status: (p, value) => (value === "active" ? p.is_active : !p.is_active),
    },
  });

  const load = async () => {
    try {
      const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
      const [cRes, sRes, bRes, iRes, spRes, pcRes, acRes, rcRes] = await Promise.all([
        api.get("/categories", { params }),
        api.get("/shift-categories", { params }),
        api.get("/beneficiary-categories", { params }),
        api.get("/intervention-categories", { params }),
        api.get("/services-products", { params }),
        api.get("/promo-codes", { params }),
        api.get("/affiliate-codes", { params }),
        api.get("/referral-codes", { params }),
      ]);
      setCollabList(cRes.data || []);
      setShiftList(sRes.data || []);
      setBenList(bRes.data || []);
      setIntList(iRes.data || []);
      setServiceList(spRes.data || []);
      setPromoList(pcRes.data || []);
      setAffiliateList(acRes.data || []);
      setReferralList(rcRes.data || []);
    } catch {
      // ignore
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedBrandId]);

  // ---- Collab category handlers
  const saveCollab = async () => {
    try {
      if (!collabForm.name.trim()) return toast.error("Nom requis");
      if (collabEditing) {
        await api.put(`/categories/${collabEditing.id}`, collabForm);
        toast.success("Catégorie mise à jour");
      } else {
        await api.post("/categories", collabForm);
        toast.success("Catégorie créée");
      }
      setCollabOpen(false);
      setCollabEditing(null);
      setCollabForm({ name: "", description: "", color: "#0055FF", is_salarie: false, annual_leave_days: 25, is_leadership: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const removeCollab = async (id) => {
    if (!window.confirm("Supprimer cette catégorie ?")) return;
    try { await api.delete(`/categories/${id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const openCollabEdit = (c) => { setCollabEditing(c); setCollabForm({ name: c.name, description: c.description || "", color: c.color, is_salarie: !!c.is_salarie, annual_leave_days: c.annual_leave_days ?? 25, is_leadership: !!c.is_leadership }); setCollabOpen(true); };
  const openCollabCreate = () => { setCollabEditing(null); setCollabForm({ name: "", description: "", color: "#0055FF", is_salarie: false, annual_leave_days: 25, is_leadership: false }); setCollabOpen(true); };

  // ---- Shift category handlers
  const saveShift = async () => {
    try {
      if (!shiftForm.name.trim()) return toast.error("Nom requis");
      if (!shiftForm.brand_ids || shiftForm.brand_ids.length === 0) return toast.error("Sélectionnez au moins un département");
      if (shiftEditing) {
        await api.put(`/shift-categories/${shiftEditing.id}`, shiftForm);
        toast.success("Catégorie de shift mise à jour");
      } else {
        await api.post("/shift-categories", shiftForm);
        toast.success("Catégorie de shift créée");
      }
      setShiftOpen(false);
      setShiftEditing(null);
      setShiftForm({ name: "", description: "", color: "#0055FF", brand_ids: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const removeShift = async (id) => {
    if (!window.confirm("Supprimer cette catégorie de shift ?")) return;
    try { await api.delete(`/shift-categories/${id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const openShiftEdit = (c) => {
    setShiftEditing(c);
    // Compat ascendante : fall back vers [brand_id] si brand_ids absent
    const ids = Array.isArray(c.brand_ids) && c.brand_ids.length > 0
      ? c.brand_ids
      : (c.brand_id ? [c.brand_id] : []);
    setShiftForm({ name: c.name, description: c.description || "", color: c.color, brand_ids: ids });
    setShiftOpen(true);
  };
  const openShiftCreate = () => {
    setShiftEditing(null);
    const defaults = selectedBrandId ? [selectedBrandId] : [];
    setShiftForm({ name: "", description: "", color: "#0055FF", brand_ids: defaults });
    setShiftOpen(true);
  };
  const toggleShiftBrand = (bid) => {
    setShiftForm((f) => ({
      ...f,
      brand_ids: f.brand_ids.includes(bid)
        ? f.brand_ids.filter((x) => x !== bid)
        : [...f.brand_ids, bid],
    }));
  };

  // ---- Beneficiary category handlers
  const saveBen = async () => {
    try {
      if (!benForm.name.trim()) return toast.error("Nom requis");
      if (benEditing) {
        await api.put(`/beneficiary-categories/${benEditing.id}`, benForm);
        toast.success("Catégorie mise à jour");
      } else {
        await api.post("/beneficiary-categories", benForm);
        toast.success("Catégorie créée");
      }
      setBenOpen(false);
      setBenEditing(null);
      setBenForm({ name: "", description: "", color: "#D97706" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const removeBen = async (id) => {
    if (!window.confirm("Supprimer cette catégorie ?")) return;
    try { await api.delete(`/beneficiary-categories/${id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const openBenEdit = (c) => { setBenEditing(c); setBenForm({ name: c.name, description: c.description || "", color: c.color }); setBenOpen(true); };
  const openBenCreate = () => { setBenEditing(null); setBenForm({ name: "", description: "", color: "#D97706" }); setBenOpen(true); };

  // ---- Intervention category handlers (mêmes règles que shifts)
  const saveInt = async () => {
    try {
      if (!intForm.name.trim()) return toast.error("Nom requis");
      if (!intForm.brand_ids || intForm.brand_ids.length === 0) return toast.error("Sélectionnez au moins un département");
      if (intEditing) {
        await api.put(`/intervention-categories/${intEditing.id}`, intForm);
        toast.success("Catégorie d'intervention mise à jour");
      } else {
        await api.post("/intervention-categories", intForm);
        toast.success("Catégorie d'intervention créée");
      }
      setIntOpen(false);
      setIntEditing(null);
      setIntForm({ name: "", description: "", color: "#7C3AED", brand_ids: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const removeInt = async (id) => {
    if (!window.confirm("Supprimer cette catégorie d'intervention ?")) return;
    try { await api.delete(`/intervention-categories/${id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const openIntEdit = (c) => {
    setIntEditing(c);
    const ids = Array.isArray(c.brand_ids) && c.brand_ids.length > 0
      ? c.brand_ids
      : (c.brand_id ? [c.brand_id] : []);
    setIntForm({ name: c.name, description: c.description || "", color: c.color, brand_ids: ids });
    setIntOpen(true);
  };
  const openIntCreate = () => {
    setIntEditing(null);
    const defaults = selectedBrandId ? [selectedBrandId] : [];
    setIntForm({ name: "", description: "", color: "#7C3AED", brand_ids: defaults });
    setIntOpen(true);
  };
  const toggleIntBrand = (bid) => {
    setIntForm((f) => ({
      ...f,
      brand_ids: f.brand_ids.includes(bid)
        ? f.brand_ids.filter((x) => x !== bid)
        : [...f.brand_ids, bid],
    }));
  };
  const importFromShifts = async () => {
    if (!window.confirm("Copier toutes les catégories de shifts visibles vers les catégories d'interventions ? Les doublons (même nom + mêmes départements) seront ignorés.")) return;
    setIntImporting(true);
    try {
      const { data } = await api.post("/intervention-categories/import-from-shifts");
      toast.success(`Import terminé : ${data.created} créée(s), ${data.skipped} ignorée(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setIntImporting(false);
    }
  };

  // ---- Services et produits handlers (catalogue KPI)
  // TTC = (Tarif HT + Frais HT) × (1 + TVA/100) + Débours
  // La TVA ne s'applique qu'au Tarif HT + Frais HT ; les débours sont hors TVA.
  const computeTtc = (ht, tva, fees = 0, disbursements = 0) => {
    const h = Number(ht || 0);
    const t = Number(tva || 0);
    const f = Number(fees || 0);
    const d = Number(disbursements || 0);
    return Math.round(((h + f) * (1 + t / 100) + d) * 100) / 100;
  };
  const formatPrice = (v) =>
    Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const saveService = async () => {
    try {
      if (!serviceForm.name.trim()) return toast.error("Nom requis");
      if (!serviceForm.brand_ids || serviceForm.brand_ids.length === 0) return toast.error("Sélectionnez au moins un département");
      const isQuote = !!serviceForm.tarif_sur_devis;
      let payload;
      if (isQuote) {
        // Tarif sur devis : les champs tarifaires ne sont pas requis. Ils
        // seront saisis manuellement à chaque transaction.
        payload = {
          ...serviceForm,
          tarif_sur_devis: true,
          price_ht: 0,
          vat_rate: 0,
          fees_ht: 0,
          disbursements: 0,
        };
      } else {
        const ht = Number(serviceForm.price_ht);
        const tva = Number(serviceForm.vat_rate);
        const fees = Number(serviceForm.fees_ht);
        const disb = Number(serviceForm.disbursements);
        if (isNaN(ht) || ht < 0) return toast.error("Prix HT invalide");
        if (isNaN(tva) || tva < 0 || tva > 100) return toast.error("Taux TVA invalide (0 à 100)");
        if (isNaN(fees) || fees < 0) return toast.error("Montant HT des frais facturés invalide (≥ 0)");
        if (isNaN(disb) || disb < 0) return toast.error("Montant des débours facturés invalide (≥ 0)");
        payload = { ...serviceForm, tarif_sur_devis: false, price_ht: ht, vat_rate: tva, fees_ht: fees, disbursements: disb };
      }
      if (serviceEditing) {
        await api.put(`/services-products/${serviceEditing.id}`, payload);
        toast.success("Fiche mise à jour");
      } else {
        await api.post("/services-products", payload);
        toast.success("Fiche créée");
      }
      setServiceOpen(false);
      setServiceEditing(null);
      setServiceForm(emptyServiceForm());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const removeService = async (id) => {
    if (!window.confirm("Supprimer définitivement cette fiche ?")) return;
    try { await api.delete(`/services-products/${id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const openServiceEdit = (c) => {
    setServiceEditing(c);
    setServiceForm({
      name: c.name || "",
      kind: c.kind === "produit" ? "produit" : "service",
      description: c.description || "",
      color: c.color || "#0EA5E9",
      brand_ids: Array.isArray(c.brand_ids) ? c.brand_ids : [],
      price_ht: Number(c.price_ht || 0),
      vat_rate: Number(c.vat_rate ?? 20),
      fees_ht: Number(c.fees_ht || 0),
      disbursements: Number(c.disbursements || 0),
      tarif_sur_devis: !!c.tarif_sur_devis,
    });
    setServiceOpen(true);
  };
  const openServiceCreate = () => {
    setServiceEditing(null);
    const defaults = selectedBrandId ? [selectedBrandId] : [];
    setServiceForm({ ...emptyServiceForm(), brand_ids: defaults });
    setServiceOpen(true);
  };
  const toggleServiceBrand = (bid) => {
    setServiceForm((f) => ({
      ...f,
      brand_ids: f.brand_ids.includes(bid)
        ? f.brand_ids.filter((x) => x !== bid)
        : [...f.brand_ids, bid],
    }));
  };

  // ---- Codes promotionnels handlers
  const loadPromoContacts = async (brandIds) => {
    const ids = Array.isArray(brandIds) ? brandIds.filter(Boolean) : (brandIds ? [brandIds] : []);
    if (ids.length === 0) { setPromoContacts([]); return []; }
    try {
      const results = await Promise.all(
        ids.map((bid) => api.get("/beneficiaries", { params: { brand_id: bid } }).then((r) => r.data || []).catch(() => []))
      );
      const seen = new Set();
      const merged = [];
      for (const list of results) {
        for (const c of list) {
          if (c && c.id && !seen.has(c.id)) { seen.add(c.id); merged.push(c); }
        }
      }
      setPromoContacts(merged);
      return merged;
    } catch { setPromoContacts([]); return []; }
  };
  // Charge la liste des services (union) pour les départements sélectionnés
  const loadServicesForBrands = async (brandIds) => {
    const ids = Array.isArray(brandIds) ? brandIds.filter(Boolean) : [];
    if (ids.length === 0) return [];
    try {
      const results = await Promise.all(
        ids.map((bid) => api.get("/services-products", { params: { brand_id: bid, kind: "service" } }).then((r) => r.data || []).catch(() => []))
      );
      const seen = new Set();
      const merged = [];
      for (const list of results) {
        for (const s of list) {
          if (s && s.id && !seen.has(s.id)) { seen.add(s.id); merged.push(s); }
        }
      }
      return merged;
    } catch { return []; }
  };
  const loadPromoServices = async (brandIds) => {
    const merged = await loadServicesForBrands(brandIds);
    setPromoServices(merged);
    return merged;
  };
  const togglePromoBrand = async (bid) => {
    const has = promoForm.brand_ids.includes(bid);
    const nextIds = has ? promoForm.brand_ids.filter((x) => x !== bid) : [...promoForm.brand_ids, bid];
    setPromoForm((f) => ({ ...f, brand_ids: nextIds }));
    const [merged, mergedServices] = await Promise.all([loadPromoContacts(nextIds), loadPromoServices(nextIds)]);
    // Si le contact sélectionné n'appartient plus à aucun département coché, on le vide
    setPromoForm((f) => {
      const validSvcIds = new Set(mergedServices.map((s) => s.id));
      const nextSvcComms = {};
      for (const [sid, val] of Object.entries(f.service_commissions || {})) {
        if (validSvcIds.has(sid)) nextSvcComms[sid] = val;
      }
      let next = { ...f, service_commissions: nextSvcComms };
      if (!f.beneficiary_id) return next;
      if (nextIds.length === 0) return { ...next, beneficiary_id: "" };
      if (!merged.some((c) => c.id === f.beneficiary_id)) return { ...next, beneficiary_id: "" };
      return next;
    });
  };
  const togglePromoServiceCommission = (sid) => {
    setPromoForm((f) => {
      const cur = f.service_commissions || {};
      const next = { ...cur };
      if (sid in next) delete next[sid];
      else next[sid] = { commission_type: "percentage", commission_value: 0 };
      // Sync case maîtresse — cf. code affiliation.
      return { ...f, service_commissions: next, commission_enabled: Object.keys(next).length > 0 };
    });
  };
  const updatePromoServiceCommission = (sid, patch) => {
    setPromoForm((f) => ({
      ...f,
      service_commissions: {
        ...(f.service_commissions || {}),
        [sid]: { ...(f.service_commissions?.[sid] || { commission_type: "percentage", commission_value: 0 }), ...patch },
      },
    }));
  };
  const openPromoCreate = () => {
    setPromoEditing(null);
    const defaultBrand = selectedBrandId || (brands[0]?.id || "");
    const ids = defaultBrand ? [defaultBrand] : [];
    const form = { ...emptyPromoForm(), brand_ids: ids };
    setPromoForm(form);
    if (ids.length) { loadPromoContacts(ids); loadPromoServices(ids); }
    setPromoOpen(true);
  };
  const openPromoEdit = (p) => {
    setPromoEditing(p);
    const ids = Array.isArray(p.brand_ids) && p.brand_ids.length > 0
      ? p.brand_ids
      : (p.brand_id ? [p.brand_id] : []);
    const svcMap = {};
    for (const sc of (p.service_commissions || [])) {
      if (sc && sc.service_id) {
        svcMap[sc.service_id] = {
          commission_type: sc.commission_type || "percentage",
          commission_value: Number(sc.commission_value ?? 0),
        };
      }
    }
    setPromoForm({
      code: p.code || "",
      brand_ids: ids,
      beneficiary_id: p.beneficiary_id || "",
      is_internal: !!p.is_internal,
      start_date: p.start_date || "",
      end_date: p.end_date || "",
      is_indefinite: !!p.is_indefinite,
      discount_type: p.discount_type || "percentage",
      discount_value: Number(p.discount_value ?? 0),
      commission_enabled: Array.isArray(p.service_commissions) && p.service_commissions.length > 0,
      commission_type: p.commission_type || "percentage",
      commission_value: Number(p.commission_value ?? 0),
      service_commissions: svcMap,
      offers: Array.isArray(p.offers) ? p.offers : [],
      manual_active: p.manual_active !== false,
      description: p.description || "",
    });
    if (ids.length) { loadPromoContacts(ids); loadPromoServices(ids); }
    setPromoOpen(true);
  };
  const generatePromoCode = async () => {
    setPromoGenerating(true);
    try {
      const prefix = (promoForm.code || "PROMO").replace(/[^A-Za-z0-9]/g, "").slice(0, 12) || "PROMO";
      const { data } = await api.get("/promo-codes/generate", { params: { prefix } });
      setPromoForm((f) => ({ ...f, code: data.code }));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setPromoGenerating(false); }
  };
  const computePromoDuration = () => {
    if (promoForm.is_indefinite) return "Indéterminée";
    if (!promoForm.start_date || !promoForm.end_date) return "—";
    try {
      const s = new Date(promoForm.start_date + "T00:00:00");
      const e = new Date(promoForm.end_date + "T00:00:00");
      const diff = Math.floor((e - s) / (1000 * 60 * 60 * 24));
      if (isNaN(diff) || diff < 0) return "—";
      const days = diff + 1;
      return `${days} jour${days > 1 ? "s" : ""}`;
    } catch { return "—"; }
  };
  const savePromo = async () => {
    if (!promoForm.code.trim()) return toast.error("Code requis");
    if (!promoForm.brand_ids || promoForm.brand_ids.length === 0) return toast.error("Sélectionnez au moins un département");
    if (!promoForm.is_internal && !promoForm.beneficiary_id) return toast.error("Contact partenaire requis");
    if (!promoForm.start_date) return toast.error("Date d'activation requise");
    if (!promoForm.is_indefinite && !promoForm.end_date) return toast.error("Date de fin requise (ou cochez « Indéterminée »)");
    const dv = Number(promoForm.discount_value);
    if (isNaN(dv) || dv < 0) return toast.error("Valeur de remise invalide");
    if (promoForm.discount_type === "percentage" && dv > 100) return toast.error("Le pourcentage ne peut dépasser 100 %");
    let commissionType = null;
    let commissionValue = null;
    // Commissions par service — TOUJOURS persistées quand des entrées sont
    // définies dans `service_commissions`, indépendamment de la case maîtresse
    // `commission_enabled`. La case maîtresse ne pilote plus que l'affichage
    // du panneau : la source de vérité est le map `{ [sid]: {type, value} }`.
    // Cela évite qu'un utilisateur ayant renseigné les valeurs mais oublié la
    // case principale (ou dont l'état s'est réinitialisé lors d'une édition)
    // se retrouve avec un code sans commission.
    const svcArr = [];
    // Un code promo interne (marketing) ne génère aucune commission, quelle
    // que soit la configuration — on force donc une liste vide.
    if (!promoForm.is_internal) {
      for (const [sid, val] of Object.entries(promoForm.service_commissions || {})) {
        const cv = Number(val?.commission_value);
        if (isNaN(cv) || cv < 0) return toast.error("Valeur de commission par service invalide");
        const ctype = val?.commission_type || "percentage";
        if (ctype === "percentage" && cv > 100) return toast.error("La commission par service en % ne peut dépasser 100 %");
        svcArr.push({ service_id: sid, commission_type: ctype, commission_value: cv });
      }
    }
    try {
      const payload = {
        code: promoForm.code.trim().toUpperCase(),
        brand_ids: promoForm.brand_ids,
        beneficiary_id: promoForm.is_internal ? null : promoForm.beneficiary_id,
        is_internal: !!promoForm.is_internal,
        start_date: promoForm.start_date,
        end_date: promoForm.is_indefinite ? null : promoForm.end_date,
        is_indefinite: promoForm.is_indefinite,
        discount_type: promoForm.discount_type,
        discount_value: dv,
        commission_type: commissionType,
        commission_value: commissionValue,
        service_commissions: svcArr,
        offers: (promoForm.offers || []).map((o) => ({
          id: o.id,
          label: (o.label || "").trim(),
          service_product_ids: o.service_product_ids || [],
          discount_type: o.discount_type || "percentage",
          discount_value: Number(o.discount_value ?? 0),
        })),
        manual_active: promoForm.manual_active,
        description: promoForm.description || "",
      };
      if (promoEditing) {
        await api.put(`/promo-codes/${promoEditing.id}`, payload);
        toast.success("Code mis à jour");
      } else {
        await api.post("/promo-codes", payload);
        toast.success("Code créé");
      }
      setPromoOpen(false);
      setPromoEditing(null);
      setPromoForm(emptyPromoForm());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const removePromo = async (p) => {
    if (!window.confirm(`Supprimer définitivement le code ${p.code} ?`)) return;
    try { await api.delete(`/promo-codes/${p.id}`); toast.success("Code supprimé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const togglePromoActive = async (p) => {
    try {
      await api.put(`/promo-codes/${p.id}`, { manual_active: !p.manual_active });
      toast.success(!p.manual_active ? "Code activé" : "Code désactivé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const promoStatusMeta = (p) => {
    if (p.is_active) return { label: "Actif", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    if (p.status === "upcoming") return { label: "À venir", cls: "bg-sky-50 text-sky-700 border-sky-200" };
    if (p.status === "expired") return { label: "Expiré", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" };
    if (p.status === "disabled") return { label: "Désactivé", cls: "bg-amber-50 text-amber-700 border-amber-200" };
    return { label: "Inactif", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" };
  };

  // ---- Codes d'affiliation handlers (miroir strict des codes promo)
  const loadAffiliateContacts = async (brandIds) => {
    const ids = Array.isArray(brandIds) ? brandIds.filter(Boolean) : (brandIds ? [brandIds] : []);
    if (ids.length === 0) { setAffiliateContacts([]); return []; }
    try {
      const results = await Promise.all(
        ids.map((bid) => api.get("/beneficiaries", { params: { brand_id: bid } }).then((r) => r.data || []).catch(() => []))
      );
      const seen = new Set();
      const merged = [];
      for (const list of results) {
        for (const c of list) {
          if (c && c.id && !seen.has(c.id)) { seen.add(c.id); merged.push(c); }
        }
      }
      setAffiliateContacts(merged);
      return merged;
    } catch { setAffiliateContacts([]); return []; }
  };
  const loadAffiliateServices = async (brandIds) => {
    const merged = await loadServicesForBrands(brandIds);
    setAffiliateServices(merged);
    return merged;
  };
  const toggleAffiliateBrand = async (bid) => {
    const has = affiliateForm.brand_ids.includes(bid);
    const nextIds = has ? affiliateForm.brand_ids.filter((x) => x !== bid) : [...affiliateForm.brand_ids, bid];
    setAffiliateForm((f) => ({ ...f, brand_ids: nextIds }));
    const [merged, mergedServices] = await Promise.all([loadAffiliateContacts(nextIds), loadAffiliateServices(nextIds)]);
    setAffiliateForm((f) => {
      const validSvcIds = new Set(mergedServices.map((s) => s.id));
      const nextSvcComms = {};
      for (const [sid, val] of Object.entries(f.service_commissions || {})) {
        if (validSvcIds.has(sid)) nextSvcComms[sid] = val;
      }
      let next = { ...f, service_commissions: nextSvcComms };
      if (!f.beneficiary_id) return next;
      if (nextIds.length === 0) return { ...next, beneficiary_id: "" };
      if (!merged.some((c) => c.id === f.beneficiary_id)) return { ...next, beneficiary_id: "" };
      return next;
    });
  };
  const toggleAffiliateServiceCommission = (sid) => {
    setAffiliateForm((f) => {
      const cur = f.service_commissions || {};
      const next = { ...cur };
      if (sid in next) delete next[sid];
      else next[sid] = { commission_type: "percentage", commission_value: 0 };
      // Sync case maîtresse : auto-active dès qu'une entrée existe pour éviter
      // que la section se replie et que l'utilisateur pense qu'elle est perdue.
      return { ...f, service_commissions: next, commission_enabled: Object.keys(next).length > 0 };
    });
  };
  const updateAffiliateServiceCommission = (sid, patch) => {
    setAffiliateForm((f) => ({
      ...f,
      service_commissions: {
        ...(f.service_commissions || {}),
        [sid]: { ...(f.service_commissions?.[sid] || { commission_type: "percentage", commission_value: 0 }), ...patch },
      },
    }));
  };
  const openAffiliateCreate = () => {
    setAffiliateEditing(null);
    const defaultBrand = selectedBrandId || (brands[0]?.id || "");
    const ids = defaultBrand ? [defaultBrand] : [];
    const form = { ...emptyAffiliateForm(), brand_ids: ids };
    setAffiliateForm(form);
    if (ids.length) { loadAffiliateContacts(ids); loadAffiliateServices(ids); }
    setAffiliateOpen(true);
  };
  const openAffiliateEdit = (p) => {
    setAffiliateEditing(p);
    const ids = Array.isArray(p.brand_ids) && p.brand_ids.length > 0
      ? p.brand_ids
      : (p.brand_id ? [p.brand_id] : []);
    const svcMap = {};
    for (const sc of (p.service_commissions || [])) {
      if (sc && sc.service_id) {
        svcMap[sc.service_id] = {
          commission_type: sc.commission_type || "percentage",
          commission_value: Number(sc.commission_value ?? 0),
        };
      }
    }
    setAffiliateForm({
      code: p.code || "",
      brand_ids: ids,
      beneficiary_id: p.beneficiary_id || "",
      start_date: p.start_date || "",
      end_date: p.end_date || "",
      is_indefinite: !!p.is_indefinite,
      discount_type: p.discount_type || "percentage",
      discount_value: Number(p.discount_value ?? 0),
      commission_enabled: Array.isArray(p.service_commissions) && p.service_commissions.length > 0,
      commission_type: p.commission_type || "percentage",
      commission_value: Number(p.commission_value ?? 0),
      service_commissions: svcMap,
      offers: Array.isArray(p.offers) ? p.offers : [],
      manual_active: p.manual_active !== false,
      description: p.description || "",
    });
    if (ids.length) { loadAffiliateContacts(ids); loadAffiliateServices(ids); }
    setAffiliateOpen(true);
  };
  const generateAffiliateCode = async () => {
    setAffiliateGenerating(true);
    try {
      const prefix = (affiliateForm.code || "AFF").replace(/[^A-Za-z0-9]/g, "").slice(0, 12) || "AFF";
      const { data } = await api.get("/affiliate-codes/generate", { params: { prefix } });
      setAffiliateForm((f) => ({ ...f, code: data.code }));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setAffiliateGenerating(false); }
  };
  const computeAffiliateDuration = () => {
    if (affiliateForm.is_indefinite) return "Indéterminée";
    if (!affiliateForm.start_date || !affiliateForm.end_date) return "—";
    try {
      const s = new Date(affiliateForm.start_date + "T00:00:00");
      const e = new Date(affiliateForm.end_date + "T00:00:00");
      const diff = Math.floor((e - s) / (1000 * 60 * 60 * 24));
      if (isNaN(diff) || diff < 0) return "—";
      const days = diff + 1;
      return `${days} jour${days > 1 ? "s" : ""}`;
    } catch { return "—"; }
  };
  const saveAffiliate = async () => {
    if (!affiliateForm.code.trim()) return toast.error("Code requis");
    if (!affiliateForm.brand_ids || affiliateForm.brand_ids.length === 0) return toast.error("Sélectionnez au moins un département");
    if (!affiliateForm.beneficiary_id) return toast.error("Contact partenaire requis");
    if (!affiliateForm.start_date) return toast.error("Date d'activation requise");
    if (!affiliateForm.is_indefinite && !affiliateForm.end_date) return toast.error("Date de fin requise (ou cochez « Indéterminée »)");
    const dv = Number(affiliateForm.discount_value);
    if (isNaN(dv) || dv < 0) return toast.error("Valeur de remise invalide");
    if (affiliateForm.discount_type === "percentage" && dv > 100) return toast.error("Le pourcentage ne peut dépasser 100 %");
    let commissionType = null;
    let commissionValue = null;
    // Commissions par service — TOUJOURS persistées quand des entrées sont
    // définies dans `service_commissions`, indépendamment de la case maîtresse
    // `commission_enabled` (cf. code promotionnel : même règle appliquée pour
    // éviter la perte silencieuse de configuration).
    const svcArr = [];
    for (const [sid, val] of Object.entries(affiliateForm.service_commissions || {})) {
      const cv = Number(val?.commission_value);
      if (isNaN(cv) || cv < 0) return toast.error("Valeur de commission par service invalide");
      const ctype = val?.commission_type || "percentage";
      if (ctype === "percentage" && cv > 100) return toast.error("La commission par service en % ne peut dépasser 100 %");
      svcArr.push({ service_id: sid, commission_type: ctype, commission_value: cv });
    }
    try {
      const payload = {
        code: affiliateForm.code.trim().toUpperCase(),
        brand_ids: affiliateForm.brand_ids,
        beneficiary_id: affiliateForm.beneficiary_id,
        start_date: affiliateForm.start_date,
        end_date: affiliateForm.is_indefinite ? null : affiliateForm.end_date,
        is_indefinite: affiliateForm.is_indefinite,
        discount_type: affiliateForm.discount_type,
        discount_value: dv,
        commission_type: commissionType,
        commission_value: commissionValue,
        service_commissions: svcArr,
        offers: (affiliateForm.offers || []).map((o) => ({
          id: o.id,
          label: (o.label || "").trim(),
          service_product_ids: o.service_product_ids || [],
          discount_type: o.discount_type || "percentage",
          discount_value: Number(o.discount_value ?? 0),
        })),
        manual_active: affiliateForm.manual_active,
        description: affiliateForm.description || "",
      };
      if (affiliateEditing) {
        await api.put(`/affiliate-codes/${affiliateEditing.id}`, payload);
        toast.success("Code mis à jour");
      } else {
        await api.post("/affiliate-codes", payload);
        toast.success("Code créé");
      }
      setAffiliateOpen(false);
      setAffiliateEditing(null);
      setAffiliateForm(emptyAffiliateForm());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const removeAffiliate = async (p) => {
    if (!window.confirm(`Supprimer définitivement le code ${p.code} ?`)) return;
    try { await api.delete(`/affiliate-codes/${p.id}`); toast.success("Code supprimé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const toggleAffiliateActive = async (p) => {
    try {
      await api.put(`/affiliate-codes/${p.id}`, { manual_active: !p.manual_active });
      toast.success(!p.manual_active ? "Code activé" : "Code désactivé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const affiliateStatusMeta = (p) => promoStatusMeta(p);

  // ---- Codes de parrainage handlers (miroir strict des codes promo/affiliation)
  const loadReferralContacts = async (brandIds) => {
    setReferralContacts([]);
    setReferralCollabs([]);
    const ids = Array.isArray(brandIds) ? brandIds.filter(Boolean) : (brandIds ? [brandIds] : []);
    if (ids.length === 0) return { benefs: [], collabs: [] };
    const [benefsResults, collabsResults] = await Promise.all([
      Promise.all(ids.map((bid) => api.get("/beneficiaries", { params: { brand_id: bid } }).then((r) => r.data || []).catch(() => []))),
      Promise.all(ids.map((bid) => api.get("/collaborateurs", { params: { brand_id: bid } }).then((r) => r.data || []).catch(() => []))),
    ]);
    const dedup = (results) => {
      const seen = new Set();
      const merged = [];
      for (const list of results) {
        for (const c of list) {
          if (c && c.id && !seen.has(c.id)) { seen.add(c.id); merged.push(c); }
        }
      }
      return merged;
    };
    const benefs = dedup(benefsResults);
    const collabs = dedup(collabsResults);
    setReferralContacts(benefs);
    setReferralCollabs(collabs);
    return { benefs, collabs };
  };
  const toggleReferralBrand = async (bid) => {
    const has = referralForm.brand_ids.includes(bid);
    const nextIds = has ? referralForm.brand_ids.filter((x) => x !== bid) : [...referralForm.brand_ids, bid];
    setReferralForm((f) => ({ ...f, brand_ids: nextIds }));
    const [{ benefs, collabs }, mergedServices] = await Promise.all([
      loadReferralContacts(nextIds),
      loadServicesForBrands(nextIds).then((s) => { setReferralServices(s); return s; }),
    ]);
    setReferralForm((f) => {
      const cleared = nextIds.length === 0;
      let next = { ...f };
      // Retire les services obsolètes de chaque offre
      const validSvcIds = new Set(mergedServices.map((s) => s.id));
      next.offers = (f.offers || []).map((o) => ({
        ...o,
        service_product_ids: (o.service_product_ids || []).filter((sid) => validSvcIds.has(sid)),
      }));
      if (cleared) return { ...next, beneficiary_id: "", collaborator_id: "" };
      if (f.beneficiary_id && !benefs.some((c) => c.id === f.beneficiary_id)) next.beneficiary_id = "";
      if (f.collaborator_id && !collabs.some((c) => c.id === f.collaborator_id)) next.collaborator_id = "";
      return next;
    });
  };
  const openReferralCreate = () => {
    setReferralEditing(null);
    const defaultBrand = selectedBrandId || (brands[0]?.id || "");
    const ids = defaultBrand ? [defaultBrand] : [];
    const form = { ...emptyReferralForm(), brand_ids: ids };
    setReferralForm(form);
    if (ids.length) {
      loadReferralContacts(ids);
      loadServicesForBrands(ids).then(setReferralServices);
    } else {
      setReferralServices([]);
    }
    setReferralOpen(true);
  };
  const openReferralEdit = (p) => {
    setReferralEditing(p);
    const ids = Array.isArray(p.brand_ids) && p.brand_ids.length > 0
      ? p.brand_ids
      : (p.brand_id ? [p.brand_id] : []);
    setReferralForm({
      code: p.code || "",
      brand_ids: ids,
      contact_type: p.contact_type || (p.collaborator_id ? "collaborator" : "beneficiary"),
      beneficiary_id: p.beneficiary_id || "",
      collaborator_id: p.collaborator_id || "",
      start_date: p.start_date || "",
      end_date: p.end_date || "",
      is_indefinite: !!p.is_indefinite,
      discount_type: p.discount_type || "percentage",
      discount_value: Number(p.discount_value ?? 0),
      offers: Array.isArray(p.offers) ? p.offers : [],
      manual_active: p.manual_active !== false,
      description: p.description || "",
    });
    if (ids.length) {
      loadReferralContacts(ids);
      loadServicesForBrands(ids).then(setReferralServices);
    } else {
      setReferralServices([]);
    }
    setReferralOpen(true);
  };
  const generateReferralCode = async () => {
    setReferralGenerating(true);
    try {
      const prefix = (referralForm.code || "PAR").replace(/[^A-Za-z0-9]/g, "").slice(0, 12) || "PAR";
      const { data } = await api.get("/referral-codes/generate", { params: { prefix } });
      setReferralForm((f) => ({ ...f, code: data.code }));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setReferralGenerating(false); }
  };
  const computeReferralDuration = () => {
    if (referralForm.is_indefinite) return "Indéterminée";
    if (!referralForm.start_date || !referralForm.end_date) return "—";
    try {
      const s = new Date(referralForm.start_date + "T00:00:00");
      const e = new Date(referralForm.end_date + "T00:00:00");
      const diff = Math.floor((e - s) / (1000 * 60 * 60 * 24));
      if (isNaN(diff) || diff < 0) return "—";
      const days = diff + 1;
      return `${days} jour${days > 1 ? "s" : ""}`;
    } catch { return "—"; }
  };
  const saveReferral = async () => {
    if (!referralForm.code.trim()) return toast.error("Code requis");
    if (!referralForm.brand_ids || referralForm.brand_ids.length === 0) return toast.error("Sélectionnez au moins un département");
    if (referralForm.contact_type === "collaborator") {
      if (!referralForm.collaborator_id) return toast.error("Collaborateur requis");
    } else {
      if (!referralForm.beneficiary_id) return toast.error("Contact partenaire requis");
    }
    if (!referralForm.start_date) return toast.error("Date d'activation requise");
    if (!referralForm.is_indefinite && !referralForm.end_date) return toast.error("Date de fin requise (ou cochez « Indéterminée »)");
    const dv = Number(referralForm.discount_value);
    if (isNaN(dv) || dv < 0) return toast.error("Valeur de remise invalide");
    if (referralForm.discount_type === "percentage" && dv > 100) return toast.error("Le pourcentage ne peut dépasser 100 %");
    try {
      const payload = {
        code: referralForm.code.trim().toUpperCase(),
        brand_ids: referralForm.brand_ids,
        contact_type: referralForm.contact_type,
        beneficiary_id: referralForm.contact_type === "beneficiary" ? referralForm.beneficiary_id : null,
        collaborator_id: referralForm.contact_type === "collaborator" ? referralForm.collaborator_id : null,
        start_date: referralForm.start_date,
        end_date: referralForm.is_indefinite ? null : referralForm.end_date,
        is_indefinite: referralForm.is_indefinite,
        discount_type: referralForm.discount_type,
        discount_value: dv,
        offers: (referralForm.offers || []).map((o) => ({
          id: o.id,
          label: (o.label || "").trim(),
          service_product_ids: o.service_product_ids || [],
          discount_type: o.discount_type || "percentage",
          discount_value: Number(o.discount_value ?? 0),
        })),
        manual_active: referralForm.manual_active,
        description: referralForm.description || "",
      };
      if (referralEditing) {
        await api.put(`/referral-codes/${referralEditing.id}`, payload);
        toast.success("Code mis à jour");
      } else {
        await api.post("/referral-codes", payload);
        toast.success("Code créé");
      }
      setReferralOpen(false);
      setReferralEditing(null);
      setReferralForm(emptyReferralForm());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const removeReferral = async (p) => {
    if (!window.confirm(`Supprimer définitivement le code ${p.code} ?`)) return;
    try { await api.delete(`/referral-codes/${p.id}`); toast.success("Code supprimé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const toggleReferralActive = async (p) => {
    try {
      await api.put(`/referral-codes/${p.id}`, { manual_active: !p.manual_active });
      toast.success(!p.manual_active ? "Code activé" : "Code désactivé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const referralStatusMeta = (p) => promoStatusMeta(p);

  return (
    <div className="space-y-6" data-testid="categories-page">
      <Tabs defaultValue={isGestionnaire ? "promos" : "collab"} className="w-full">
        <StickyPageHeader testid="categories-sticky-header">
        <div>
          <p className="label-uppercase mb-2">Organisation</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900">
            Catégories
          </h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">Profils de collaborateurs et catégories de shifts par département</p>
        </div>

        <div className="w-full overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="categories-tabs-scroll">
          <TabsList className="w-max">
            {canSeeGeneralCats && (<>
            <TabsTrigger value="collab" data-testid="tab-collab-cats"><Layers size={14} className="mr-1.5" /> Collaborateurs</TabsTrigger>
            <TabsTrigger value="shift" data-testid="tab-shift-cats"><Tag size={14} className="mr-1.5" /> Shifts</TabsTrigger>
            <TabsTrigger value="ben" data-testid="tab-ben-cats"><Contact size={14} className="mr-1.5" /> Bénéficiaires</TabsTrigger>
            <TabsTrigger value="intervention" data-testid="tab-intervention-cats"><Briefcase size={14} className="mr-1.5" /> Interventions</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services-cats"><Package size={14} className="mr-1.5" /> Services & Produits</TabsTrigger>
            </>)}
            <TabsTrigger value="promos" data-testid="tab-promos-cats"><Ticket size={14} className="mr-1.5" /> Codes Promotionnels</TabsTrigger>
            <TabsTrigger value="affiliates" data-testid="tab-affiliates-cats"><Link2 size={14} className="mr-1.5" /> Codes Affiliation</TabsTrigger>
            <TabsTrigger value="referrals" data-testid="tab-referrals-cats"><Users size={14} className="mr-1.5" /> Codes Parrainage</TabsTrigger>
          </TabsList>
        </div>
        </StickyPageHeader>

        {/* --------- Collaborateur categories --------- */}
        <TabsContent value="collab" className="space-y-4">
          {canManage && (
            <div className="flex justify-end">
              <Dialog open={collabOpen} onOpenChange={setCollabOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openCollabCreate} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm" data-testid="new-category-btn">
                    <Plus size={14} className="mr-1" /> Nouvelle catégorie
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{collabEditing ? "Modifier" : "Nouvelle catégorie collaborateur"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Nom</Label>
                      <Input value={collabForm.name} onChange={(e) => setCollabForm({ ...collabForm, name: e.target.value })} placeholder="Ex: CDI temps plein" data-testid="category-name" />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Description</Label>
                      <Textarea value={collabForm.description} onChange={(e) => setCollabForm({ ...collabForm, description: e.target.value })} rows={2} />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Couleur</Label>
                      <div className="flex gap-2 flex-wrap">
                        {COLORS.map((c) => (
                          <button key={c} type="button" onClick={() => setCollabForm({ ...collabForm, color: c })}
                            className={`h-8 w-8 rounded-sm border-2 ${collabForm.color === c ? "border-zinc-900" : "border-transparent"}`}
                            style={{ backgroundColor: c }} data-testid={`category-color-${c}`} />
                        ))}
                      </div>
                    </div>
                    <div className="border-t border-zinc-200 pt-3 space-y-3">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={collabForm.is_salarie}
                          onChange={(e) => setCollabForm({ ...collabForm, is_salarie: e.target.checked })}
                          className="h-4 w-4 rounded border-zinc-300"
                          data-testid="category-is-salarie"
                        />
                        <span><strong>Catégorie salariée</strong> — active le suivi du solde de congés</span>
                      </label>
                      {collabForm.is_salarie && (
                        <div>
                          <Label className="label-uppercase mb-1.5 block">Jours de congés annuels</Label>
                          <Input
                            type="number"
                            min="0"
                            max="365"
                            value={collabForm.annual_leave_days}
                            onChange={(e) => setCollabForm({ ...collabForm, annual_leave_days: parseInt(e.target.value || "0", 10) })}
                            data-testid="category-annual-days"
                          />
                          <p className="text-[11px] text-zinc-500 font-mono mt-1">25 jours par défaut en France</p>
                        </div>
                      )}
                      <label className="flex items-start gap-2 text-sm cursor-pointer pt-1">
                        <input
                          type="checkbox"
                          checked={collabForm.is_leadership}
                          onChange={(e) => setCollabForm({ ...collabForm, is_leadership: e.target.checked })}
                          className="h-4 w-4 rounded border-zinc-300 mt-0.5"
                          data-testid="category-is-leadership"
                        />
                        <span>
                          <strong>Catégorie de direction / management</strong>
                          <span className="block text-[11px] text-zinc-500 font-mono mt-0.5">
                            Les collaborateurs affectés à cette catégorie sont visibles par les managers dans la liste, mais leur dossier ne peut être ni consulté ni modifié depuis un compte Manager.
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCollabOpen(false)}>Annuler</Button>
                    <Button onClick={saveCollab} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="category-save-btn">Enregistrer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <QuickSearchBar
            value={collabFilters}
            onChange={setCollabFilters}
            placeholder="Rechercher une catégorie collaborateur…"
            testid="collab-cats-search"
            filters={[]}
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-zinc-500 font-mono" data-testid="collab-cats-result-count">
              {filteredCollab.length} résultat{filteredCollab.length > 1 ? "s" : ""} sur {collabList.length}
            </p>
            <ExportCsvButton
              filename="categories_collaborateurs"
              testid="collab-cats-export-csv"
              rows={filteredCollab}
              columns={[
                { label: "Nom", value: (c) => c.name },
                { label: "Description", value: (c) => c.description || "" },
                { label: "Salariée", value: (c) => (c.is_salarie ? "Oui" : "Non") },
                { label: "Jours de congés annuels", value: (c) => (c.is_salarie ? (c.annual_leave_days ?? 25) : "") },
                { label: "Catégorie de direction", value: (c) => (c.is_leadership ? "Oui" : "Non") },
              ]}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCollab.map((c) => (
              <CategoryCard key={c.id} c={c} canManage={canManage} isAdmin={isAdmin} onEdit={openCollabEdit} onDelete={removeCollab} prefix="category" />
            ))}
            {filteredCollab.length === 0 && (
              <div className="col-span-3 text-center py-12 text-sm text-zinc-500 font-mono bg-white border border-zinc-200 rounded-sm">
                {collabList.length === 0 ? "Aucune catégorie. Créez votre première." : "Aucune catégorie ne correspond à votre recherche."}
              </div>
            )}
          </div>
        </TabsContent>

        {/* --------- Shift categories --------- */}
        <TabsContent value="shift" className="space-y-4">
          {canManage && (
            <div className="flex justify-end">
              <Dialog open={shiftOpen} onOpenChange={setShiftOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openShiftCreate} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm" data-testid="new-shift-category-btn">
                    <Plus size={14} className="mr-1" /> Nouvelle catégorie de shift
                  </Button>
                </DialogTrigger>
                <DialogContent className="flex flex-col max-h-[90vh] p-0">
                  <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
                    <DialogTitle>{shiftEditing ? "Modifier la catégorie de shift" : "Nouvelle catégorie de shift"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1" data-testid="shift-cat-dialog-scroll">
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Intitulé</Label>
                      <Input value={shiftForm.name} onChange={(e) => setShiftForm({ ...shiftForm, name: e.target.value })} placeholder="Ex: Prospection commerciale, Réunion, RDV client…" data-testid="shift-category-name" />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Départements concernés</Label>
                      <p className="text-[11px] text-zinc-500 font-mono mb-2">Cochez un ou plusieurs départements — la catégorie sera disponible sur chacun d&apos;eux.</p>
                      <div className="space-y-2 border border-zinc-200 rounded-sm p-3 max-h-48 overflow-y-auto" data-testid="shift-category-brand-list">
                        {brands.length === 0 && (
                          <p className="text-xs text-zinc-500 font-mono">Aucun département configuré.</p>
                        )}
                        {brands.map((b) => (
                          <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox
                              checked={shiftForm.brand_ids.includes(b.id)}
                              onCheckedChange={() => toggleShiftBrand(b.id)}
                              data-testid={`shift-category-brand-${b.id}`}
                            />
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: b.color }} />
                            <span>{b.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Description</Label>
                      <Textarea value={shiftForm.description} onChange={(e) => setShiftForm({ ...shiftForm, description: e.target.value })} rows={2} />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Couleur</Label>
                      <div className="flex gap-2 flex-wrap">
                        {COLORS.map((c) => (
                          <button key={c} type="button" onClick={() => setShiftForm({ ...shiftForm, color: c })}
                            className={`h-8 w-8 rounded-sm border-2 ${shiftForm.color === c ? "border-zinc-900" : "border-transparent"}`}
                            style={{ backgroundColor: c }} data-testid={`shift-category-color-${c}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
                    <Button variant="outline" onClick={() => setShiftOpen(false)}>Annuler</Button>
                    <Button onClick={saveShift} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="shift-category-save-btn">Enregistrer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <QuickSearchBar
            value={shiftFilters}
            onChange={setShiftFilters}
            placeholder="Rechercher une catégorie de shift…"
            testid="shift-cats-search"
            filters={[
              { key: "brand_id", label: "Département", type: "select", placeholder: "Tous les départements", options: brands.map((b) => ({ value: b.id, label: b.name })) },
            ]}
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-zinc-500 font-mono" data-testid="shift-cats-result-count">
              {filteredShift.length} résultat{filteredShift.length > 1 ? "s" : ""} sur {shiftList.length}
            </p>
            <ExportCsvButton
              filename="categories_shifts"
              testid="shift-cats-export-csv"
              rows={filteredShift}
              columns={[
                { label: "Nom", value: (c) => c.name },
                { label: "Description", value: (c) => c.description || "" },
                { label: "Départements", value: (c) => {
                  const ids = c.brand_ids && c.brand_ids.length ? c.brand_ids : (c.brand_id ? [c.brand_id] : []);
                  return ids.map((id) => brands.find((b) => b.id === id)?.name).filter(Boolean).join(", ");
                } },
              ]}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredShift.map((c) => (
              <CategoryCard key={c.id} c={c} brandsList={brands} canManage={canManage} isAdmin={isAdmin} onEdit={openShiftEdit} onDelete={removeShift} prefix="shift-cat" />
            ))}
            {filteredShift.length === 0 && (
              <div className="col-span-3 text-center py-12 text-sm text-zinc-500 font-mono bg-white border border-zinc-200 rounded-sm">
                {shiftList.length === 0 ? "Aucune catégorie de shift. Définissez vos intitulés (Prospection, Réunion, RDV client…)." : "Aucune catégorie ne correspond à votre recherche."}
              </div>
            )}
          </div>
        </TabsContent>

        {/* --------- Beneficiary categories --------- */}
        <TabsContent value="ben" className="space-y-4">
          {canManage && (
            <div className="flex justify-end">
              <Dialog open={benOpen} onOpenChange={setBenOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openBenCreate} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-sm" data-testid="new-ben-category-btn">
                    <Plus size={14} className="mr-1" /> Nouvelle catégorie bénéficiaire
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{benEditing ? "Modifier la catégorie bénéficiaire" : "Nouvelle catégorie bénéficiaire"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Intitulé</Label>
                      <Input value={benForm.name} onChange={(e) => setBenForm({ ...benForm, name: e.target.value })} placeholder="Ex : Clientèle, Partenaire, Fournisseur, Prospect…" data-testid="ben-category-name" />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Description</Label>
                      <Textarea value={benForm.description} onChange={(e) => setBenForm({ ...benForm, description: e.target.value })} rows={2} />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Couleur</Label>
                      <div className="flex gap-2 flex-wrap">
                        {COLORS.map((c) => (
                          <button key={c} type="button" onClick={() => setBenForm({ ...benForm, color: c })}
                            className={`h-8 w-8 rounded-sm border-2 ${benForm.color === c ? "border-zinc-900" : "border-transparent"}`}
                            style={{ backgroundColor: c }} data-testid={`ben-category-color-${c}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setBenOpen(false)}>Annuler</Button>
                    <Button onClick={saveBen} className="bg-[#D97706] hover:bg-[#B45309] text-white" data-testid="ben-category-save-btn">Enregistrer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <QuickSearchBar
            value={benFilters}
            onChange={setBenFilters}
            placeholder="Rechercher une catégorie bénéficiaire…"
            testid="ben-cats-search"
            filters={[]}
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-zinc-500 font-mono" data-testid="ben-cats-result-count">
              {filteredBen.length} résultat{filteredBen.length > 1 ? "s" : ""} sur {benList.length}
            </p>
            <ExportCsvButton
              filename="categories_beneficiaires"
              testid="ben-cats-export-csv"
              rows={filteredBen}
              columns={[
                { label: "Nom", value: (c) => c.name },
                { label: "Description", value: (c) => c.description || "" },
              ]}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredBen.map((c) => (
              <CategoryCard key={c.id} c={c} canManage={canManage} isAdmin={isAdmin} onEdit={openBenEdit} onDelete={removeBen} prefix="ben-cat" />
            ))}
            {filteredBen.length === 0 && (
              <div className="col-span-3 text-center py-12 text-sm text-zinc-500 font-mono bg-white border border-zinc-200 rounded-sm">
                {benList.length === 0 ? "Aucune catégorie de bénéficiaire. Créez vos profils (Clientèle, Partenaire…)." : "Aucune catégorie ne correspond à votre recherche."}
              </div>
            )}
          </div>
        </TabsContent>

        {/* --------- Intervention categories (Agenda départements) --------- */}
        <TabsContent value="intervention" className="space-y-4">
          {canManage && (
            <div className="flex justify-end gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={importFromShifts}
                disabled={intImporting}
                data-testid="intervention-category-import-btn"
                title="Copier les catégories de shifts vers les interventions"
              >
                <Copy size={14} className="mr-1" />
                {intImporting ? "Import…" : "Copier depuis les Shifts"}
              </Button>
              <Dialog open={intOpen} onOpenChange={setIntOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openIntCreate} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-sm" data-testid="new-intervention-category-btn">
                    <Plus size={14} className="mr-1" /> Nouvelle catégorie d&apos;intervention
                  </Button>
                </DialogTrigger>
                <DialogContent className="flex flex-col max-h-[90vh] p-0">
                  <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
                    <DialogTitle>{intEditing ? "Modifier la catégorie d'intervention" : "Nouvelle catégorie d'intervention"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1" data-testid="intervention-cat-dialog-scroll">
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Intitulé</Label>
                      <Input value={intForm.name} onChange={(e) => setIntForm({ ...intForm, name: e.target.value })} placeholder="Ex : Consulting, Formation, Maintenance, Audit…" data-testid="intervention-category-name" />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Départements concernés</Label>
                      <p className="text-[11px] text-zinc-500 font-mono mb-2">Cochez un ou plusieurs départements — la catégorie sera disponible sur chacun d&apos;eux dans l&apos;Agenda départements.</p>
                      <div className="space-y-2 border border-zinc-200 rounded-sm p-3 max-h-48 overflow-y-auto" data-testid="intervention-category-brand-list">
                        {brands.length === 0 && (
                          <p className="text-xs text-zinc-500 font-mono">Aucun département configuré.</p>
                        )}
                        {brands.map((b) => (
                          <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox
                              checked={intForm.brand_ids.includes(b.id)}
                              onCheckedChange={() => toggleIntBrand(b.id)}
                              data-testid={`intervention-category-brand-${b.id}`}
                            />
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: b.color }} />
                            <span>{b.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Description</Label>
                      <Textarea value={intForm.description} onChange={(e) => setIntForm({ ...intForm, description: e.target.value })} rows={2} />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Couleur</Label>
                      <div className="flex gap-2 flex-wrap">
                        {COLORS.map((c) => (
                          <button key={c} type="button" onClick={() => setIntForm({ ...intForm, color: c })}
                            className={`h-8 w-8 rounded-sm border-2 ${intForm.color === c ? "border-zinc-900" : "border-transparent"}`}
                            style={{ backgroundColor: c }} data-testid={`intervention-category-color-${c}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
                    <Button variant="outline" onClick={() => setIntOpen(false)}>Annuler</Button>
                    <Button onClick={saveInt} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" data-testid="intervention-category-save-btn">Enregistrer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <QuickSearchBar
            value={intFilters}
            onChange={setIntFilters}
            placeholder="Rechercher une catégorie d'intervention…"
            testid="intervention-cats-search"
            filters={[
              { key: "brand_id", label: "Département", type: "select", placeholder: "Tous les départements", options: brands.map((b) => ({ value: b.id, label: b.name })) },
            ]}
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-zinc-500 font-mono" data-testid="intervention-cats-result-count">
              {filteredInt.length} résultat{filteredInt.length > 1 ? "s" : ""} sur {intList.length}
            </p>
            <ExportCsvButton
              filename="categories_interventions"
              testid="intervention-cats-export-csv"
              rows={filteredInt}
              columns={[
                { label: "Nom", value: (c) => c.name },
                { label: "Description", value: (c) => c.description || "" },
                { label: "Départements", value: (c) => {
                  const ids = c.brand_ids && c.brand_ids.length ? c.brand_ids : (c.brand_id ? [c.brand_id] : []);
                  return ids.map((id) => brands.find((b) => b.id === id)?.name).filter(Boolean).join(", ");
                } },
              ]}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredInt.map((c) => (
              <CategoryCard key={c.id} c={c} brandsList={brands} canManage={canManage} isAdmin={isAdmin} onEdit={openIntEdit} onDelete={removeInt} prefix="intervention-cat" />
            ))}
            {filteredInt.length === 0 && (
              <div className="col-span-3 text-center py-12 text-sm text-zinc-500 font-mono bg-white border border-zinc-200 rounded-sm">
                {intList.length === 0
                  ? "Aucune catégorie d'intervention. Créez vos types (Consulting, Formation, Maintenance…) ou utilisez « Copier depuis les Shifts »."
                  : "Aucune catégorie ne correspond à votre recherche."}
              </div>
            )}
          </div>
        </TabsContent>

        {/* --------- Services et produits (catalogue KPI) --------- */}
        <TabsContent value="services" className="space-y-4">
          {canManage && (
            <div className="flex justify-end">
              <Dialog open={serviceOpen} onOpenChange={setServiceOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openServiceCreate} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white rounded-sm" data-testid="new-service-product-btn">
                    <Plus size={14} className="mr-1" /> Nouveau service / produit
                  </Button>
                </DialogTrigger>
                <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-lg">
                  <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
                    <DialogTitle>{serviceEditing ? "Modifier la fiche" : "Nouveau service / produit"}</DialogTitle>
                    <DialogDescription className="text-[11px] text-zinc-500 font-mono">
                      Renseignez le type, les départements, le prix HT et le taux de TVA. Le prix TTC est calculé automatiquement.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1" data-testid="service-product-dialog-scroll">
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Type</Label>
                      <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden" role="tablist">
                        <button
                          type="button"
                          onClick={() => setServiceForm({ ...serviceForm, kind: "service" })}
                          className={`px-4 py-1.5 text-sm ${serviceForm.kind === "service" ? "bg-[#0EA5E9] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                          data-testid="service-product-kind-service"
                        >
                          Service
                        </button>
                        <button
                          type="button"
                          onClick={() => setServiceForm({ ...serviceForm, kind: "produit" })}
                          className={`px-4 py-1.5 text-sm border-l border-zinc-200 ${serviceForm.kind === "produit" ? "bg-[#0EA5E9] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                          data-testid="service-product-kind-produit"
                        >
                          Produit
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Intitulé</Label>
                      <Input
                        value={serviceForm.name}
                        onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                        placeholder={serviceForm.kind === "produit" ? "Ex : Menu déjeuner, Bouteille 75cl…" : "Ex : Prestation conseil 1h, Audit RH…"}
                        data-testid="service-product-name"
                      />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Départements concernés</Label>
                      <p className="text-[11px] text-zinc-500 font-mono mb-2">Cochez un ou plusieurs départements — le service/produit sera rattaché à chacun d&apos;eux pour les KPI.</p>
                      <div className="space-y-2 border border-zinc-200 rounded-sm p-3 max-h-48 overflow-y-auto" data-testid="service-product-brand-list">
                        {brands.length === 0 && (
                          <p className="text-xs text-zinc-500 font-mono">Aucun département configuré.</p>
                        )}
                        {brands.map((b) => (
                          <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox
                              checked={serviceForm.brand_ids.includes(b.id)}
                              onCheckedChange={() => toggleServiceBrand(b.id)}
                              data-testid={`service-product-brand-${b.id}`}
                            />
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: b.color }} />
                            <span>{b.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-sm border border-amber-200 bg-amber-50/60 px-3 py-2.5 flex items-start gap-3" data-testid="service-product-tarif-sur-devis-row">
                      <Checkbox
                        checked={!!serviceForm.tarif_sur_devis}
                        onCheckedChange={(v) => setServiceForm({ ...serviceForm, tarif_sur_devis: !!v })}
                        data-testid="service-product-tarif-sur-devis"
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <Label
                          htmlFor="service-product-tarif-sur-devis"
                          onClick={() => setServiceForm({ ...serviceForm, tarif_sur_devis: !serviceForm.tarif_sur_devis })}
                          className="text-sm font-semibold text-amber-900 cursor-pointer select-none"
                        >
                          Tarif sur devis
                        </Label>
                        <p className="text-[11px] text-amber-800/80 font-mono mt-0.5">
                          Cochez si le tarif est établi au cas par cas. Les champs Prix HT, TVA, Frais et Débours seront saisis manuellement à chaque transaction.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Prix HT (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={serviceForm.price_ht}
                          onChange={(e) => setServiceForm({ ...serviceForm, price_ht: e.target.value })}
                          data-testid="service-product-price-ht"
                          disabled={!!serviceForm.tarif_sur_devis}
                          className={serviceForm.tarif_sur_devis ? "bg-zinc-100 text-zinc-400 cursor-not-allowed" : ""}
                        />
                      </div>
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Taux TVA (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={serviceForm.vat_rate}
                          onChange={(e) => setServiceForm({ ...serviceForm, vat_rate: e.target.value })}
                          data-testid="service-product-vat-rate"
                          disabled={!!serviceForm.tarif_sur_devis}
                          className={serviceForm.tarif_sur_devis ? "bg-zinc-100 text-zinc-400 cursor-not-allowed" : ""}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="label-uppercase mb-1.5 block">
                          Montant HT des frais facturés (€) {!serviceForm.tarif_sur_devis && <span className="text-red-500">*</span>}
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          required={!serviceForm.tarif_sur_devis}
                          value={serviceForm.fees_ht}
                          onChange={(e) => setServiceForm({ ...serviceForm, fees_ht: e.target.value })}
                          data-testid="service-product-fees-ht"
                          placeholder="0.00"
                          disabled={!!serviceForm.tarif_sur_devis}
                          className={serviceForm.tarif_sur_devis ? "bg-zinc-100 text-zinc-400 cursor-not-allowed" : ""}
                        />
                        <p className="text-[10px] font-mono text-zinc-500 mt-1">Soumis à la TVA (avec le Tarif HT)</p>
                      </div>
                      <div>
                        <Label className="label-uppercase mb-1.5 block">
                          Montant débours facturés (€) {!serviceForm.tarif_sur_devis && <span className="text-red-500">*</span>}
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          required={!serviceForm.tarif_sur_devis}
                          value={serviceForm.disbursements}
                          onChange={(e) => setServiceForm({ ...serviceForm, disbursements: e.target.value })}
                          data-testid="service-product-disbursements"
                          placeholder="0.00"
                          disabled={!!serviceForm.tarif_sur_devis}
                          className={serviceForm.tarif_sur_devis ? "bg-zinc-100 text-zinc-400 cursor-not-allowed" : ""}
                        />
                        <p className="text-[10px] font-mono text-zinc-500 mt-1">Jamais soumis à la TVA</p>
                      </div>
                    </div>
                    {serviceForm.tarif_sur_devis ? (
                      <div className="rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 flex items-center justify-between" data-testid="service-product-ttc-preview-quote">
                        <span className="text-[11px] font-mono uppercase tracking-wider text-amber-800">Prix TTC calculé</span>
                        <span className="text-sm font-semibold text-amber-900 font-mono">Sur devis</span>
                      </div>
                    ) : (
                      <div className="rounded-sm border border-[#0EA5E9]/30 bg-[#0EA5E9]/5 px-3 py-2 flex items-center justify-between" data-testid="service-product-ttc-preview">
                        <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Prix TTC calculé</span>
                        <span className="text-lg font-semibold text-zinc-900 font-mono">
                          {formatPrice(computeTtc(serviceForm.price_ht, serviceForm.vat_rate, serviceForm.fees_ht, serviceForm.disbursements))} €
                        </span>
                      </div>
                    )}
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Description</Label>
                      <Textarea
                        value={serviceForm.description}
                        onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                        rows={2}
                        placeholder="Détails complémentaires (facultatif)"
                        data-testid="service-product-description"
                      />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Couleur</Label>
                      <div className="flex gap-2 flex-wrap">
                        {COLORS.map((c) => (
                          <button key={c} type="button" onClick={() => setServiceForm({ ...serviceForm, color: c })}
                            className={`h-8 w-8 rounded-sm border-2 ${serviceForm.color === c ? "border-zinc-900" : "border-transparent"}`}
                            style={{ backgroundColor: c }} data-testid={`service-product-color-${c}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
                    <Button variant="outline" onClick={() => setServiceOpen(false)}>Annuler</Button>
                    <Button onClick={saveService} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="service-product-save-btn">Enregistrer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <QuickSearchBar
            value={serviceFilters}
            onChange={setServiceFilters}
            placeholder="Rechercher un service ou un produit…"
            testid="services-cats-search"
            filters={[
              { key: "kind", label: "Type", type: "select", placeholder: "Tous", options: [
                { value: "service", label: "Service" },
                { value: "produit", label: "Produit" },
              ] },
              { key: "brand_id", label: "Département", type: "select", placeholder: "Tous les départements", options: brands.map((b) => ({ value: b.id, label: b.name })) },
            ]}
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-zinc-500 font-mono" data-testid="services-cats-result-count">
              {filteredServices.length} résultat{filteredServices.length > 1 ? "s" : ""} sur {serviceList.length}
            </p>
            <ExportCsvButton
              filename="services_et_produits"
              testid="services-cats-export-csv"
              rows={filteredServices}
              columns={[
                { label: "Type", value: (c) => (c.kind === "produit" ? "Produit" : "Service") },
                { label: "Nom", value: (c) => c.name },
                { label: "Description", value: (c) => c.description || "" },
                { label: "Tarif sur devis", value: (c) => (c.tarif_sur_devis ? "Oui" : "Non") },
                { label: "Départements", value: (c) => (c.brand_ids || []).map((id) => brands.find((b) => b.id === id)?.name).filter(Boolean).join(", ") },
                { label: "Prix HT (€)", value: (c) => (c.tarif_sur_devis ? "Sur devis" : Number(c.price_ht || 0).toFixed(2)) },
                { label: "TVA (%)", value: (c) => (c.tarif_sur_devis ? "Sur devis" : Number(c.vat_rate || 0).toFixed(2)) },
                { label: "Frais HT (€)", value: (c) => (c.tarif_sur_devis ? "Sur devis" : Number(c.fees_ht || 0).toFixed(2)) },
                { label: "Débours (€)", value: (c) => (c.tarif_sur_devis ? "Sur devis" : Number(c.disbursements || 0).toFixed(2)) },
                { label: "Prix TTC (€)", value: (c) => (c.tarif_sur_devis ? "Sur devis" : Number(c.price_ttc || 0).toFixed(2)) },
              ]}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredServices.map((c) => {
              const chips = (c.brand_ids || []).map((id) => brands.find((b) => b.id === id)).filter(Boolean);
              const kindLabel = c.kind === "produit" ? "Produit" : "Service";
              return (
                <div key={c.id} className="bg-white border border-zinc-200 rounded-sm p-5 hover:border-zinc-300" data-testid={`service-product-${c.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="h-6 w-1 rounded-sm" style={{ backgroundColor: c.color || "#0EA5E9" }} />
                      <Package size={14} className="text-zinc-400" />
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider ${
                        c.kind === "produit"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-sky-50 text-sky-700 border-sky-200"
                      }`}>
                        {kindLabel}
                      </span>
                      {c.tarif_sur_devis && (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider bg-amber-50 text-amber-800 border-amber-200"
                          data-testid={`service-product-quote-badge-${c.id}`}
                          title="Le tarif est établi au cas par cas, saisi manuellement à chaque transaction"
                        >
                          Sur devis
                        </span>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex gap-1">
                        {isAdmin && (
                          <Button variant="ghost" size="sm" onClick={() => openServiceEdit(c)} data-testid={`edit-service-product-${c.id}`}>
                            <Pencil size={12} />
                          </Button>
                        )}
                        {isAdmin && (
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeService(c.id)} data-testid={`delete-service-product-${c.id}`}>
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  <h3 className="font-display text-lg font-semibold text-zinc-900">{c.name}</h3>
                  {c.description && <p className="text-sm text-zinc-600 mt-1">{c.description}</p>}
                  {c.tarif_sur_devis ? (
                    <div
                      className="mt-3 border border-amber-200 rounded-sm p-2 bg-amber-50/60 flex items-center justify-center"
                      data-testid={`service-product-quote-summary-${c.id}`}
                    >
                      <p className="text-sm font-mono font-semibold text-amber-800 uppercase tracking-wider">
                        Tarif sur devis
                      </p>
                    </div>
                  ) : (
                    <div className="mt-3 grid grid-cols-3 gap-2 text-center border border-zinc-100 rounded-sm p-2 bg-zinc-50">
                      <div>
                        <p className="text-[10px] label-uppercase text-zinc-500">HT</p>
                        <p className="text-sm font-mono font-semibold text-zinc-900">{formatPrice(c.price_ht)} €</p>
                      </div>
                      <div>
                        <p className="text-[10px] label-uppercase text-zinc-500">TVA</p>
                        <p className="text-sm font-mono font-semibold text-zinc-900">{Number(c.vat_rate || 0).toFixed(2)}%</p>
                      </div>
                      <div>
                        <p className="text-[10px] label-uppercase text-[#0EA5E9]">TTC</p>
                        <p className="text-sm font-mono font-semibold text-[#0284C7]">{formatPrice(c.price_ttc)} €</p>
                      </div>
                    </div>
                  )}
                  {!c.tarif_sur_devis && (Number(c.fees_ht || 0) > 0 || Number(c.disbursements || 0) > 0) && (
                    <div className="mt-2 grid grid-cols-2 gap-2 text-center border border-zinc-100 rounded-sm p-2 bg-white" data-testid={`service-product-fees-disb-${c.id}`}>
                      <div>
                        <p className="text-[10px] label-uppercase text-zinc-500">Frais HT</p>
                        <p className="text-sm font-mono font-semibold text-zinc-900">{formatPrice(c.fees_ht)} €</p>
                      </div>
                      <div>
                        <p className="text-[10px] label-uppercase text-zinc-500">Débours</p>
                        <p className="text-sm font-mono font-semibold text-zinc-900">{formatPrice(c.disbursements)} €</p>
                      </div>
                    </div>
                  )}
                  {chips.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`service-product-brand-chips-${c.id}`}>
                      {chips.map((b) => (
                        <span key={b.id} className="inline-flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 bg-zinc-50 border border-zinc-200 rounded-sm text-zinc-700">
                          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: b.color }} />
                          {b.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {filteredServices.length === 0 && (
              <div className="col-span-3 text-center py-12 text-sm text-zinc-500 font-mono bg-white border border-zinc-200 rounded-sm">
                {serviceList.length === 0
                  ? "Aucun service ni produit. Créez votre premier — il alimentera les KPI de facturation."
                  : "Aucune fiche ne correspond à votre recherche."}
              </div>
            )}
          </div>
        </TabsContent>

        {/* --------- Codes Promotionnels --------- */}
        <TabsContent value="promos" className="space-y-4">
          {canManage && (
            <div className="flex justify-end">
              <Dialog open={promoOpen} onOpenChange={setPromoOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openPromoCreate} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-sm" data-testid="new-promo-code-btn">
                    <Plus size={14} className="mr-1" /> Nouveau code promo
                  </Button>
                </DialogTrigger>
                <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-lg">
                  <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
                    <DialogTitle>{promoEditing ? "Modifier le code promo" : "Nouveau code promo"}</DialogTitle>
                    <DialogDescription className="text-[11px] text-zinc-500 font-mono">
                      Chaque code est associé à un ou plusieurs départements et, par défaut, à un contact partenaire.
                      Cochez « Code promo interne » pour une promotion marketing sans contact ni commission.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1">
                    {/* Code promo interne (marketing) — pas de partenaire, aucune commission */}
                    <label
                      className="flex items-start gap-2 text-sm cursor-pointer rounded-sm border border-zinc-200 bg-zinc-50/60 px-3 py-2.5"
                      data-testid="promo-code-internal-toggle"
                    >
                      <Checkbox
                        checked={!!promoForm.is_internal}
                        onCheckedChange={(v) => setPromoForm((f) => ({
                          ...f,
                          is_internal: !!v,
                          // Si on active l'option interne, on vide le contact + les commissions
                          beneficiary_id: v ? "" : f.beneficiary_id,
                          commission_enabled: v ? false : f.commission_enabled,
                          service_commissions: v ? {} : f.service_commissions,
                        }))}
                        data-testid="promo-code-internal-checkbox"
                      />
                      <span className="leading-tight">
                        <strong>Code promo interne</strong>
                        <br />
                        <span className="text-[11px] text-zinc-500 font-mono">
                          Code édité en interne (promotion marketing) — pas de contact partenaire,
                          aucune commission générée, uniquement une remise commerciale au client.
                        </span>
                      </span>
                    </label>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">ID du code promo</Label>
                      <div className="flex gap-2">
                        <Input
                          value={promoForm.code}
                          onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                          placeholder="ex : SUMMER-2026"
                          className="uppercase tracking-wider"
                          style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}
                          data-testid="promo-code-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={generatePromoCode}
                          disabled={promoGenerating}
                          data-testid="promo-code-generate-btn"
                          title="Générer un code unique"
                        >
                          <RefreshCw size={14} className={promoGenerating ? "animate-spin" : ""} />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Départements associés</Label>
                      <p className="text-[11px] text-zinc-500 font-mono mb-2">Cochez un ou plusieurs départements — le code sera disponible sur chacun d&apos;eux.</p>
                      <div className="space-y-2 border border-zinc-200 rounded-sm p-3 max-h-48 overflow-y-auto" data-testid="promo-code-brand-list">
                        {brands.length === 0 && (
                          <p className="text-xs text-zinc-500 font-mono">Aucun département configuré.</p>
                        )}
                        {brands.map((b) => (
                          <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox
                              checked={promoForm.brand_ids.includes(b.id)}
                              onCheckedChange={() => togglePromoBrand(b.id)}
                              data-testid={`promo-code-brand-${b.id}`}
                            />
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: b.color }} />
                            <span>{b.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    {!promoForm.is_internal && (
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Contact partenaire</Label>
                        <BeneficiaryCombobox
                          beneficiaries={promoContacts}
                          categoryName="Partenaire"
                          value={promoForm.beneficiary_id}
                          onChange={(v) => setPromoForm({ ...promoForm, beneficiary_id: v })}
                          placeholder={promoForm.brand_ids.length > 0 ? "Choisir un partenaire" : "Sélectionnez d'abord un département"}
                          searchPlaceholder="Rechercher un partenaire…"
                          disabled={promoForm.brand_ids.length === 0}
                          data-testid="promo-code-contact-select"
                        />
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Date d&apos;activation</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full justify-start font-normal"
                              data-testid="promo-code-start-date"
                            >
                              <CalendarDays size={14} className="mr-2 text-zinc-500" />
                              {promoForm.start_date
                                ? formatDate(parseIsoDate(promoForm.start_date), "dd MMM yyyy", { locale: frLocale })
                                : <span className="text-zinc-400">Choisir…</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="p-0 w-auto">
                            <Calendar
                              mode="single"
                              locale={frLocale}
                              selected={promoForm.start_date ? parseIsoDate(promoForm.start_date) : undefined}
                              onSelect={(d) => d && setPromoForm({ ...promoForm, start_date: formatDate(d, "yyyy-MM-dd") })}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Date de fin</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full justify-start font-normal"
                              disabled={promoForm.is_indefinite}
                              data-testid="promo-code-end-date"
                            >
                              <CalendarDays size={14} className="mr-2 text-zinc-500" />
                              {promoForm.is_indefinite
                                ? <span className="text-zinc-400">Indéterminée</span>
                                : (promoForm.end_date
                                    ? formatDate(parseIsoDate(promoForm.end_date), "dd MMM yyyy", { locale: frLocale })
                                    : <span className="text-zinc-400">Choisir…</span>)}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="p-0 w-auto">
                            <Calendar
                              mode="single"
                              locale={frLocale}
                              selected={promoForm.end_date ? parseIsoDate(promoForm.end_date) : undefined}
                              onSelect={(d) => {
                                if (!d) return;
                                const iso = formatDate(d, "yyyy-MM-dd");
                                setPromoForm((f) => {
                                  const next = { ...f, end_date: iso };
                                  // Si la date de fin est antérieure à la date d'activation, aligner la date d'activation.
                                  if (f.start_date && iso < f.start_date) next.start_date = iso;
                                  return next;
                                });
                              }}
                              defaultMonth={promoForm.end_date ? parseIsoDate(promoForm.end_date) : (promoForm.start_date ? parseIsoDate(promoForm.start_date) : undefined)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={promoForm.is_indefinite}
                        onCheckedChange={(v) => setPromoForm({ ...promoForm, is_indefinite: !!v, end_date: v ? "" : promoForm.end_date })}
                        data-testid="promo-code-indefinite"
                      />
                      <span>Durée <strong>indéterminée</strong> (sans date de fin)</span>
                    </label>
                    <div className="rounded-sm border border-[#7C3AED]/30 bg-[#7C3AED]/5 px-3 py-2 flex items-center justify-between" data-testid="promo-code-duration-preview">
                      <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1.5">
                        <CalendarDays size={12} /> Durée de validité
                      </span>
                      <span className="text-sm font-semibold text-zinc-900 font-mono">
                        {computePromoDuration()}
                      </span>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Type de remise</Label>
                      <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setPromoForm({ ...promoForm, discount_type: "percentage" })}
                          className={`px-4 py-1.5 text-sm ${promoForm.discount_type === "percentage" ? "bg-[#7C3AED] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                          data-testid="promo-code-discount-percentage"
                        >
                          Pourcentage (%)
                        </button>
                        <button
                          type="button"
                          onClick={() => setPromoForm({ ...promoForm, discount_type: "amount" })}
                          className={`px-4 py-1.5 text-sm border-l border-zinc-200 ${promoForm.discount_type === "amount" ? "bg-[#7C3AED] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                          data-testid="promo-code-discount-amount"
                        >
                          Montant fixe (€)
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Valeur de la remise</Label>
                      <div className="relative">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={promoForm.discount_value}
                          onChange={(e) => setPromoForm({ ...promoForm, discount_value: e.target.value })}
                          data-testid="promo-code-discount-value"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-zinc-500 pointer-events-none">
                          {promoForm.discount_type === "percentage" ? "%" : "€"}
                        </span>
                      </div>
                    </div>
                    {/* Commission du contact partenaire — activation + valeurs par service */}
                    {!promoForm.is_internal && (
                    <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50/60" data-testid="promo-svc-commissions-section">
                      <label className="flex items-start gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={promoForm.commission_enabled}
                          onCheckedChange={(v) => setPromoForm((f) => ({
                            ...f,
                            commission_enabled: !!v,
                            // Décocher la case maîtresse ⇒ réinitialise le map des
                            // commissions par service (voir explication sur le code
                            // affiliation ci-dessous — même règle métier).
                            service_commissions: v ? f.service_commissions : {},
                          }))}
                          data-testid="promo-code-commission-enabled"
                        />
                        <span className="leading-tight">
                          <strong>Commission du contact partenaire</strong>
                          <br />
                          <span className="text-[11px] text-zinc-500 font-mono">Cochez pour activer une rémunération versée au contact. Définissez le taux ou le montant HT service par service ci-dessous.</span>
                        </span>
                      </label>
                      {promoForm.commission_enabled && (
                        <div className="mt-3 pt-3 border-t border-zinc-200">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="leading-tight">
                              <div className="text-sm font-semibold text-zinc-900">Commissions par service</div>
                              <div className="text-[11px] text-zinc-500 font-mono">Une commission différenciée pour chaque service des départements sélectionnés.</div>
                            </div>
                            <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 shrink-0">
                              {Object.keys(promoForm.service_commissions || {}).length}/{promoServices.length} actif{Object.keys(promoForm.service_commissions || {}).length > 1 ? "s" : ""}
                            </span>
                          </div>
                          {promoForm.brand_ids.length === 0 ? (
                            <p className="text-xs text-zinc-500 font-mono py-2">Sélectionnez d&apos;abord au moins un département.</p>
                          ) : promoServices.length === 0 ? (
                            <p className="text-xs text-zinc-500 font-mono py-2">Aucun service catalogué pour ces départements.</p>
                          ) : (
                            <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="promo-svc-commissions-list">
                              {promoServices.map((svc) => {
                                const entry = promoForm.service_commissions?.[svc.id];
                                const enabled = !!entry;
                                const svcColor = svc.color || "#7C3AED";
                                return (
                                  <div key={svc.id} className="border border-zinc-100 rounded-sm p-2 bg-white" data-testid={`promo-svc-comm-row-${svc.id}`}>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <Checkbox
                                        checked={enabled}
                                        onCheckedChange={() => togglePromoServiceCommission(svc.id)}
                                        data-testid={`promo-svc-comm-toggle-${svc.id}`}
                                      />
                                      <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: svcColor }} />
                                      <span className="text-sm text-zinc-800 truncate">{svc.name}</span>
                                    </label>
                                    {enabled && (
                                      <div className="grid grid-cols-[auto,1fr] gap-2 mt-2 pl-6">
                                        <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden self-start">
                                          <button
                                            type="button"
                                            onClick={() => updatePromoServiceCommission(svc.id, { commission_type: "percentage" })}
                                            className={`px-3 py-1 text-xs ${entry.commission_type === "percentage" ? "bg-[#7C3AED] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                                            data-testid={`promo-svc-comm-type-pct-${svc.id}`}
                                          >Taux %</button>
                                          <button
                                            type="button"
                                            onClick={() => updatePromoServiceCommission(svc.id, { commission_type: "amount" })}
                                            className={`px-3 py-1 text-xs border-l border-zinc-200 ${entry.commission_type === "amount" ? "bg-[#7C3AED] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                                            data-testid={`promo-svc-comm-type-amt-${svc.id}`}
                                          >Montant HT</button>
                                        </div>
                                        <div className="relative">
                                          <Input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={entry.commission_value}
                                            onChange={(e) => updatePromoServiceCommission(svc.id, { commission_value: e.target.value })}
                                            className="h-8 text-xs"
                                            data-testid={`promo-svc-comm-value-${svc.id}`}
                                          />
                                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-zinc-500 pointer-events-none">
                                            {entry.commission_type === "percentage" ? "%" : "€ HT"}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    )}
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={promoForm.manual_active}
                        onCheckedChange={(v) => setPromoForm({ ...promoForm, manual_active: !!v })}
                        data-testid="promo-code-manual-active"
                      />
                      <span>Code <strong>actif</strong> (décochez pour désactiver manuellement)</span>
                    </label>
                    <div className="border-t border-zinc-100 pt-4">
                      <OffersEditor
                        value={promoForm.offers}
                        onChange={(next) => setPromoForm({ ...promoForm, offers: next })}
                        services={promoServices}
                        testid="promo-offer"
                      />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Description</Label>
                      <Textarea
                        value={promoForm.description}
                        onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                        rows={2}
                        placeholder="Contexte, conditions, campagne… (facultatif)"
                        data-testid="promo-code-description"
                      />
                    </div>
                  </div>
                  <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
                    <Button variant="outline" onClick={() => setPromoOpen(false)}>Annuler</Button>
                    <Button onClick={savePromo} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" data-testid="promo-code-save-btn">Enregistrer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <QuickSearchBar
            value={promoFilters}
            onChange={setPromoFilters}
            placeholder="Rechercher un code, un contact, une description…"
            testid="promos-cats-search"
            filters={[
              { key: "status", label: "Statut", type: "select", placeholder: "Tous", options: [
                { value: "active", label: "Actifs" },
                { value: "inactive", label: "Inactifs" },
              ] },
              { key: "brand_id", label: "Département", type: "select", placeholder: "Tous les départements", options: brands.map((b) => ({ value: b.id, label: b.name })) },
            ]}
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-zinc-500 font-mono" data-testid="promos-cats-result-count">
              {filteredPromos.length} résultat{filteredPromos.length > 1 ? "s" : ""} sur {promoList.length}
            </p>
            <ExportCsvButton
              filename="codes_promotionnels"
              testid="promos-cats-export-csv"
              rows={filteredPromos}
              columns={[
                { label: "Code", value: (p) => p.code },
                { label: "Départements", value: (p) => (p.brand_names && p.brand_names.length ? p.brand_names.join(", ") : (p.brand_name || "")) },
                { label: "Contact partenaire", value: (p) => p.is_internal ? "Interne (marketing)" : (p.beneficiary_name || "") },
                { label: "Date activation", value: (p) => formatDateFR(p.start_date) },
                { label: "Date fin", value: (p) => p.is_indefinite ? "Indéterminée" : formatDateFR(p.end_date) },
                { label: "Durée (jours)", value: (p) => p.duration_days ?? "Indéterminée" },
                { label: "Remise", value: (p) => `${p.discount_value}${p.discount_type === "percentage" ? "%" : "€"}` },
                { label: "Offres", value: (p) => offersToCsv(p.offers) },
                { label: "Commission contact partenaire", value: (p) => p.commission_type ? `${Number(p.commission_value || 0).toLocaleString("fr-FR", { minimumFractionDigits: p.commission_type === "amount" ? 2 : 0, maximumFractionDigits: 2 })} ${p.commission_type === "percentage" ? "%" : "€ HT"}` : "" },
                { label: "Commissions par service", value: (p) => (p.service_commissions || []).map((sc) => `${sc.service_name || sc.service_id}: ${Number(sc.commission_value || 0).toLocaleString("fr-FR", { minimumFractionDigits: sc.commission_type === "amount" ? 2 : 0, maximumFractionDigits: 2 })} ${sc.commission_type === "percentage" ? "%" : "€ HT"}`).join(" | ") },
                { label: "Statut", value: (p) => p.is_active ? "Actif" : (p.status || "Inactif") },
              ]}
            />
          </div>
          <HorizontalScrollContainer testId="promo-codes-table" className="bg-white border border-zinc-200 rounded-sm">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                <tr>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Départements</th>
                  <th className="text-left px-3 py-2">Contact partenaire</th>
                  <th className="text-left px-3 py-2">Activation</th>
                  <th className="text-left px-3 py-2">Fin</th>
                  <th className="text-right px-3 py-2">Durée</th>
                  <th className="text-right px-3 py-2">Remise</th>
                  <th className="text-left px-3 py-2">Offres</th>
                  <th className="text-right px-3 py-2">Commission</th>
                  <th className="text-left px-3 py-2">Commissions / service</th>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-left px-3 py-2">Statut</th>
                  {canManage && <th className="px-3 py-2 w-28"></th>}
                </tr>
              </thead>
              <tbody>
                {filteredPromos.map((p) => {
                  const meta = promoStatusMeta(p);
                  const discountLabel = `${Number(p.discount_value || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} ${p.discount_type === "percentage" ? "%" : "€"}`;
                  const brandChips = (p.brand_names && p.brand_names.length
                    ? p.brand_names.map((name, i) => ({ name, color: (p.brand_colors && p.brand_colors[i]) || "#7C3AED" }))
                    : (p.brand_name ? [{ name: p.brand_name, color: p.brand_color || "#7C3AED" }] : []));
                  const svcComms = Array.isArray(p.service_commissions) ? p.service_commissions : [];
                  return (
                    <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`promo-code-${p.id}`}>
                      <td className="px-3 py-2">
                        <div className="inline-flex items-center gap-1.5">
                          <Ticket size={12} className="text-[#7C3AED] flex-shrink-0" />
                          <span className="font-mono text-xs font-semibold text-zinc-900 tracking-wider">{p.code}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {brandChips.length === 0 ? (
                          <span className="text-xs text-zinc-500">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {brandChips.map((bc, i) => (
                              <span key={`${p.id}-b-${i}`} className="inline-flex items-center gap-1.5 text-[11px] text-zinc-700 border border-zinc-200 rounded-sm px-1.5 py-0.5 bg-zinc-50">
                                <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: bc.color }} />
                                {bc.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-700">
                        {p.is_internal ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-sm border border-[#7C3AED]/30 bg-[#7C3AED]/10 text-[#6D28D9] px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider"
                            data-testid={`promo-code-internal-badge-${p.id}`}
                            title="Code promo interne — pas de contact partenaire, aucune commission"
                          >
                            <Sparkles size={10} />
                            Interne
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1">
                            <Contact size={11} className="text-zinc-400" />
                            {p.beneficiary_name || "—"}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-600">{formatDateFR(p.start_date) || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-600">
                        {p.is_indefinite ? <span className="italic text-zinc-500">Indéterminée</span> : (formatDateFR(p.end_date) || "—")}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[11px] text-zinc-500">
                        {p.duration_days != null ? `${p.duration_days} j` : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="font-mono font-semibold text-[#7C3AED]">{discountLabel}</span>
                      </td>
                      <td className="px-3 py-2" data-testid={`promo-code-offers-cell-${p.id}`}>
                        <OffersCell offers={p.offers} accent="#7C3AED" />
                      </td>
                      <td className="px-3 py-2 text-right" data-testid={`promo-code-commission-cell-${p.id}`}>
                        {p.commission_type ? (
                          <span className="font-mono text-[11px] text-emerald-700">
                            {Number(p.commission_value || 0).toLocaleString("fr-FR", { minimumFractionDigits: p.commission_type === "amount" ? 2 : 0, maximumFractionDigits: 2 })}
                            {" "}
                            {p.commission_type === "percentage" ? "%" : "€ HT"}
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2" data-testid={`promo-code-svc-commissions-cell-${p.id}`}>
                        {svcComms.length === 0 ? (
                          <span className="text-xs text-zinc-400">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 max-w-[240px]">
                            {svcComms.map((sc, i) => {
                              const isPct = sc.commission_type === "percentage";
                              const suffix = isPct ? "%" : "€ HT";
                              const val = Number(sc.commission_value || 0).toLocaleString("fr-FR", { minimumFractionDigits: isPct ? 0 : 2, maximumFractionDigits: 2 });
                              return (
                                <span
                                  key={`${p.id}-sc-${i}`}
                                  className="inline-flex items-center gap-1 text-[11px] text-zinc-700 border border-zinc-200 rounded-sm px-1.5 py-0.5 bg-zinc-50"
                                  title={`${sc.service_name || sc.service_id}: ${val} ${suffix}`}
                                >
                                  <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: sc.service_color || "#7C3AED" }} />
                                  <span className="truncate max-w-[110px]">{sc.service_name || sc.service_id}</span>
                                  <span className="font-mono text-emerald-700">{val} {suffix}</span>
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-500 max-w-[220px] truncate" title={p.description || ""}>
                        {p.description || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider ${meta.cls}`} data-testid={`promo-code-status-${p.id}`}>
                          {meta.label}
                        </span>
                      </td>
                      {canManage && (
                        <td className="px-3 py-2">
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => togglePromoActive(p)} title={p.manual_active ? "Désactiver" : "Activer"} data-testid={`toggle-promo-code-${p.id}`}>
                              <Tag size={12} className={p.manual_active ? "text-emerald-600" : "text-zinc-400"} />
                            </Button>
                            {isAdmin && (
                              <Button variant="ghost" size="sm" onClick={() => openPromoEdit(p)} data-testid={`edit-promo-code-${p.id}`}>
                                <Pencil size={12} />
                              </Button>
                            )}
                            {isAdmin && (
                              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removePromo(p)} data-testid={`delete-promo-code-${p.id}`}>
                                <Trash2 size={12} />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {filteredPromos.length === 0 && (
                  <tr>
                    <td colSpan={canManage ? 13 : 12} className="px-3 py-8 text-center text-sm text-zinc-500 font-mono">
                      {promoList.length === 0
                        ? "Aucun code promo. Créez votre premier code pour animer vos partenariats."
                        : "Aucun code ne correspond à votre recherche."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </HorizontalScrollContainer>
        </TabsContent>

        {/* --------- Codes d'affiliation --------- */}
        <TabsContent value="affiliates" className="space-y-4">
          {canManage && (
            <div className="flex justify-end">
              <Dialog open={affiliateOpen} onOpenChange={setAffiliateOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openAffiliateCreate} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white rounded-sm" data-testid="new-affiliate-code-btn">
                    <Plus size={14} className="mr-1" /> Nouveau code d&apos;affiliation
                  </Button>
                </DialogTrigger>
                <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-lg">
                  <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
                    <DialogTitle>{affiliateEditing ? "Modifier le code d'affiliation" : "Nouveau code d'affiliation"}</DialogTitle>
                    <DialogDescription className="text-[11px] text-zinc-500 font-mono">
                      Chaque code est associé à un ou plusieurs départements et à un contact partenaire affilié.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1">
                    <div>
                      <Label className="label-uppercase mb-1.5 block">ID du code d&apos;affiliation</Label>
                      <div className="flex gap-2">
                        <Input
                          value={affiliateForm.code}
                          onChange={(e) => setAffiliateForm({ ...affiliateForm, code: e.target.value.toUpperCase() })}
                          placeholder="ex : AFF-PARTNER01"
                          className="uppercase tracking-wider"
                          style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}
                          data-testid="affiliate-code-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={generateAffiliateCode}
                          disabled={affiliateGenerating}
                          data-testid="affiliate-code-generate-btn"
                          title="Générer un code unique"
                        >
                          <RefreshCw size={14} className={affiliateGenerating ? "animate-spin" : ""} />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Départements associés</Label>
                      <p className="text-[11px] text-zinc-500 font-mono mb-2">Cochez un ou plusieurs départements — le code sera disponible sur chacun d&apos;eux.</p>
                      <div className="space-y-2 border border-zinc-200 rounded-sm p-3 max-h-48 overflow-y-auto" data-testid="affiliate-code-brand-list">
                        {brands.length === 0 && (
                          <p className="text-xs text-zinc-500 font-mono">Aucun département configuré.</p>
                        )}
                        {brands.map((b) => (
                          <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox
                              checked={affiliateForm.brand_ids.includes(b.id)}
                              onCheckedChange={() => toggleAffiliateBrand(b.id)}
                              data-testid={`affiliate-code-brand-${b.id}`}
                            />
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: b.color }} />
                            <span>{b.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Contact partenaire</Label>
                      <BeneficiaryCombobox
                        beneficiaries={affiliateContacts}
                        categoryName="Partenaire"
                        value={affiliateForm.beneficiary_id}
                        onChange={(v) => setAffiliateForm({ ...affiliateForm, beneficiary_id: v })}
                        placeholder={affiliateForm.brand_ids.length > 0 ? "Choisir un partenaire" : "Sélectionnez d'abord un département"}
                        searchPlaceholder="Rechercher un partenaire…"
                        disabled={affiliateForm.brand_ids.length === 0}
                        data-testid="affiliate-code-contact-select"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Date d&apos;activation</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full justify-start font-normal"
                              data-testid="affiliate-code-start-date"
                            >
                              <CalendarDays size={14} className="mr-2 text-zinc-500" />
                              {affiliateForm.start_date
                                ? formatDate(parseIsoDate(affiliateForm.start_date), "dd MMM yyyy", { locale: frLocale })
                                : <span className="text-zinc-400">Choisir…</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="p-0 w-auto">
                            <Calendar
                              mode="single"
                              locale={frLocale}
                              selected={affiliateForm.start_date ? parseIsoDate(affiliateForm.start_date) : undefined}
                              onSelect={(d) => d && setAffiliateForm({ ...affiliateForm, start_date: formatDate(d, "yyyy-MM-dd") })}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Date de fin</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full justify-start font-normal"
                              disabled={affiliateForm.is_indefinite}
                              data-testid="affiliate-code-end-date"
                            >
                              <CalendarDays size={14} className="mr-2 text-zinc-500" />
                              {affiliateForm.is_indefinite
                                ? <span className="text-zinc-400">Indéterminée</span>
                                : (affiliateForm.end_date
                                    ? formatDate(parseIsoDate(affiliateForm.end_date), "dd MMM yyyy", { locale: frLocale })
                                    : <span className="text-zinc-400">Choisir…</span>)}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="p-0 w-auto">
                            <Calendar
                              mode="single"
                              locale={frLocale}
                              selected={affiliateForm.end_date ? parseIsoDate(affiliateForm.end_date) : undefined}
                              onSelect={(d) => {
                                if (!d) return;
                                const iso = formatDate(d, "yyyy-MM-dd");
                                setAffiliateForm((f) => {
                                  const next = { ...f, end_date: iso };
                                  if (f.start_date && iso < f.start_date) next.start_date = iso;
                                  return next;
                                });
                              }}
                              defaultMonth={affiliateForm.end_date ? parseIsoDate(affiliateForm.end_date) : (affiliateForm.start_date ? parseIsoDate(affiliateForm.start_date) : undefined)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={affiliateForm.is_indefinite}
                        onCheckedChange={(v) => setAffiliateForm({ ...affiliateForm, is_indefinite: !!v, end_date: v ? "" : affiliateForm.end_date })}
                        data-testid="affiliate-code-indefinite"
                      />
                      <span>Durée <strong>indéterminée</strong> (sans date de fin)</span>
                    </label>
                    <div className="rounded-sm border border-[#0EA5E9]/30 bg-[#0EA5E9]/5 px-3 py-2 flex items-center justify-between" data-testid="affiliate-code-duration-preview">
                      <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1.5">
                        <CalendarDays size={12} /> Durée de validité
                      </span>
                      <span className="text-sm font-semibold text-zinc-900 font-mono">
                        {computeAffiliateDuration()}
                      </span>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Type de remise</Label>
                      <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setAffiliateForm({ ...affiliateForm, discount_type: "percentage" })}
                          className={`px-4 py-1.5 text-sm ${affiliateForm.discount_type === "percentage" ? "bg-[#0EA5E9] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                          data-testid="affiliate-code-discount-percentage"
                        >
                          Pourcentage (%)
                        </button>
                        <button
                          type="button"
                          onClick={() => setAffiliateForm({ ...affiliateForm, discount_type: "amount" })}
                          className={`px-4 py-1.5 text-sm border-l border-zinc-200 ${affiliateForm.discount_type === "amount" ? "bg-[#0EA5E9] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                          data-testid="affiliate-code-discount-amount"
                        >
                          Montant fixe (€)
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Valeur de la remise</Label>
                      <div className="relative">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={affiliateForm.discount_value}
                          onChange={(e) => setAffiliateForm({ ...affiliateForm, discount_value: e.target.value })}
                          data-testid="affiliate-code-discount-value"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-zinc-500 pointer-events-none">
                          {affiliateForm.discount_type === "percentage" ? "%" : "€"}
                        </span>
                      </div>
                    </div>
                    {/* Commission du contact affilié — activation + valeurs par service */}
                    <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50/60" data-testid="affiliate-svc-commissions-section">
                      <label className="flex items-start gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={affiliateForm.commission_enabled}
                          onCheckedChange={(v) => setAffiliateForm((f) => ({
                            ...f,
                            commission_enabled: !!v,
                            // Décocher la case maîtresse ⇒ réinitialise les commissions
                            // par service. Sans ce clear, une entrée orpheline serait
                            // persistée (le save ne dépend plus de la case).
                            service_commissions: v ? f.service_commissions : {},
                          }))}
                          data-testid="affiliate-code-commission-enabled"
                        />
                        <span className="leading-tight">
                          <strong>Commission du contact affilié</strong>
                          <br />
                          <span className="text-[11px] text-zinc-500 font-mono">Cochez pour activer une rémunération versée à l&apos;affilié. Définissez le taux ou le montant HT service par service ci-dessous.</span>
                        </span>
                      </label>
                      {affiliateForm.commission_enabled && (
                        <div className="mt-3 pt-3 border-t border-zinc-200">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="leading-tight">
                              <div className="text-sm font-semibold text-zinc-900">Commissions par service</div>
                              <div className="text-[11px] text-zinc-500 font-mono">Une commission différenciée pour chaque service des départements sélectionnés.</div>
                            </div>
                            <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 shrink-0">
                              {Object.keys(affiliateForm.service_commissions || {}).length}/{affiliateServices.length} actif{Object.keys(affiliateForm.service_commissions || {}).length > 1 ? "s" : ""}
                            </span>
                          </div>
                          {affiliateForm.brand_ids.length === 0 ? (
                            <p className="text-xs text-zinc-500 font-mono py-2">Sélectionnez d&apos;abord au moins un département.</p>
                          ) : affiliateServices.length === 0 ? (
                            <p className="text-xs text-zinc-500 font-mono py-2">Aucun service catalogué pour ces départements.</p>
                          ) : (
                            <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="affiliate-svc-commissions-list">
                              {affiliateServices.map((svc) => {
                                const entry = affiliateForm.service_commissions?.[svc.id];
                                const enabled = !!entry;
                                const svcColor = svc.color || "#0EA5E9";
                                return (
                                  <div key={svc.id} className="border border-zinc-100 rounded-sm p-2 bg-white" data-testid={`affiliate-svc-comm-row-${svc.id}`}>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <Checkbox
                                        checked={enabled}
                                        onCheckedChange={() => toggleAffiliateServiceCommission(svc.id)}
                                        data-testid={`affiliate-svc-comm-toggle-${svc.id}`}
                                      />
                                      <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: svcColor }} />
                                      <span className="text-sm text-zinc-800 truncate">{svc.name}</span>
                                    </label>
                                    {enabled && (
                                      <div className="grid grid-cols-[auto,1fr] gap-2 mt-2 pl-6">
                                        <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden self-start">
                                          <button
                                            type="button"
                                            onClick={() => updateAffiliateServiceCommission(svc.id, { commission_type: "percentage" })}
                                            className={`px-3 py-1 text-xs ${entry.commission_type === "percentage" ? "bg-[#0EA5E9] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                                            data-testid={`affiliate-svc-comm-type-pct-${svc.id}`}
                                          >Taux %</button>
                                          <button
                                            type="button"
                                            onClick={() => updateAffiliateServiceCommission(svc.id, { commission_type: "amount" })}
                                            className={`px-3 py-1 text-xs border-l border-zinc-200 ${entry.commission_type === "amount" ? "bg-[#0EA5E9] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                                            data-testid={`affiliate-svc-comm-type-amt-${svc.id}`}
                                          >Montant HT</button>
                                        </div>
                                        <div className="relative">
                                          <Input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={entry.commission_value}
                                            onChange={(e) => updateAffiliateServiceCommission(svc.id, { commission_value: e.target.value })}
                                            className="h-8 text-xs"
                                            data-testid={`affiliate-svc-comm-value-${svc.id}`}
                                          />
                                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-zinc-500 pointer-events-none">
                                            {entry.commission_type === "percentage" ? "%" : "€ HT"}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={affiliateForm.manual_active}
                        onCheckedChange={(v) => setAffiliateForm({ ...affiliateForm, manual_active: !!v })}
                        data-testid="affiliate-code-manual-active"
                      />
                      <span>Code <strong>actif</strong> (décochez pour désactiver manuellement)</span>
                    </label>
                    <div className="border-t border-zinc-100 pt-4">
                      <OffersEditor
                        value={affiliateForm.offers}
                        onChange={(next) => setAffiliateForm({ ...affiliateForm, offers: next })}
                        services={affiliateServices}
                        testid="affiliate-offer"
                      />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Description</Label>
                      <Textarea
                        value={affiliateForm.description}
                        onChange={(e) => setAffiliateForm({ ...affiliateForm, description: e.target.value })}
                        rows={2}
                        placeholder="Contexte, conditions, campagne d'affiliation… (facultatif)"
                        data-testid="affiliate-code-description"
                      />
                    </div>
                  </div>
                  <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
                    <Button variant="outline" onClick={() => setAffiliateOpen(false)}>Annuler</Button>
                    <Button onClick={saveAffiliate} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="affiliate-code-save-btn">Enregistrer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <QuickSearchBar
            value={affiliateFilters}
            onChange={setAffiliateFilters}
            placeholder="Rechercher un code, un contact, une description…"
            testid="affiliates-cats-search"
            filters={[
              { key: "status", label: "Statut", type: "select", placeholder: "Tous", options: [
                { value: "active", label: "Actifs" },
                { value: "inactive", label: "Inactifs" },
              ] },
              { key: "brand_id", label: "Département", type: "select", placeholder: "Tous les départements", options: brands.map((b) => ({ value: b.id, label: b.name })) },
            ]}
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-zinc-500 font-mono" data-testid="affiliates-cats-result-count">
              {filteredAffiliates.length} résultat{filteredAffiliates.length > 1 ? "s" : ""} sur {affiliateList.length}
            </p>
            <ExportCsvButton
              filename="codes_affiliation"
              testid="affiliates-cats-export-csv"
              rows={filteredAffiliates}
              columns={[
                { label: "Code", value: (p) => p.code },
                { label: "Départements", value: (p) => (p.brand_names && p.brand_names.length ? p.brand_names.join(", ") : (p.brand_name || "")) },
                { label: "Contact partenaire", value: (p) => p.beneficiary_name || "" },
                { label: "Date activation", value: (p) => formatDateFR(p.start_date) },
                { label: "Date fin", value: (p) => p.is_indefinite ? "Indéterminée" : formatDateFR(p.end_date) },
                { label: "Durée (jours)", value: (p) => p.duration_days ?? "Indéterminée" },
                { label: "Remise", value: (p) => `${p.discount_value}${p.discount_type === "percentage" ? "%" : "€"}` },
                { label: "Offres", value: (p) => offersToCsv(p.offers) },
                { label: "Commission contact affilié", value: (p) => p.commission_type ? `${Number(p.commission_value || 0).toLocaleString("fr-FR", { minimumFractionDigits: p.commission_type === "amount" ? 2 : 0, maximumFractionDigits: 2 })} ${p.commission_type === "percentage" ? "%" : "€ HT"}` : "" },
                { label: "Commissions par service", value: (p) => (p.service_commissions || []).map((sc) => `${sc.service_name || sc.service_id}: ${Number(sc.commission_value || 0).toLocaleString("fr-FR", { minimumFractionDigits: sc.commission_type === "amount" ? 2 : 0, maximumFractionDigits: 2 })} ${sc.commission_type === "percentage" ? "%" : "€ HT"}`).join(" | ") },
                { label: "Statut", value: (p) => p.is_active ? "Actif" : (p.status || "Inactif") },
              ]}
            />
          </div>
          <HorizontalScrollContainer testId="affiliate-codes-table" className="bg-white border border-zinc-200 rounded-sm">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                <tr>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Départements</th>
                  <th className="text-left px-3 py-2">Contact affilié</th>
                  <th className="text-left px-3 py-2">Activation</th>
                  <th className="text-left px-3 py-2">Fin</th>
                  <th className="text-right px-3 py-2">Durée</th>
                  <th className="text-right px-3 py-2">Remise</th>
                  <th className="text-left px-3 py-2">Offres</th>
                  <th className="text-right px-3 py-2">Commission</th>
                  <th className="text-left px-3 py-2">Commissions / service</th>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-left px-3 py-2">Statut</th>
                  {canManage && <th className="px-3 py-2 w-28"></th>}
                </tr>
              </thead>
              <tbody>
                {filteredAffiliates.map((p) => {
                  const meta = affiliateStatusMeta(p);
                  const discountLabel = `${Number(p.discount_value || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} ${p.discount_type === "percentage" ? "%" : "€"}`;
                  const brandChips = (p.brand_names && p.brand_names.length
                    ? p.brand_names.map((name, i) => ({ name, color: (p.brand_colors && p.brand_colors[i]) || "#0EA5E9" }))
                    : (p.brand_name ? [{ name: p.brand_name, color: p.brand_color || "#0EA5E9" }] : []));
                  const svcComms = Array.isArray(p.service_commissions) ? p.service_commissions : [];
                  return (
                    <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`affiliate-code-${p.id}`}>
                      <td className="px-3 py-2">
                        <div className="inline-flex items-center gap-1.5">
                          <Link2 size={12} className="text-[#0EA5E9] flex-shrink-0" />
                          <span className="font-mono text-xs font-semibold text-zinc-900 tracking-wider">{p.code}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {brandChips.length === 0 ? (
                          <span className="text-xs text-zinc-500">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {brandChips.map((bc, i) => (
                              <span key={`${p.id}-b-${i}`} className="inline-flex items-center gap-1.5 text-[11px] text-zinc-700 border border-zinc-200 rounded-sm px-1.5 py-0.5 bg-zinc-50">
                                <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: bc.color }} />
                                {bc.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-700">
                        <span className="inline-flex items-center gap-1">
                          <Contact size={11} className="text-zinc-400" />
                          {p.beneficiary_name || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-600">{formatDateFR(p.start_date) || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-600">
                        {p.is_indefinite ? <span className="italic text-zinc-500">Indéterminée</span> : (formatDateFR(p.end_date) || "—")}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[11px] text-zinc-500">
                        {p.duration_days != null ? `${p.duration_days} j` : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="font-mono font-semibold text-[#0EA5E9]">{discountLabel}</span>
                      </td>
                      <td className="px-3 py-2" data-testid={`affiliate-code-offers-cell-${p.id}`}>
                        <OffersCell offers={p.offers} accent="#0EA5E9" />
                      </td>
                      <td className="px-3 py-2 text-right" data-testid={`affiliate-code-commission-cell-${p.id}`}>
                        {p.commission_type ? (
                          <span className="font-mono text-[11px] text-emerald-700">
                            {Number(p.commission_value || 0).toLocaleString("fr-FR", { minimumFractionDigits: p.commission_type === "amount" ? 2 : 0, maximumFractionDigits: 2 })}
                            {" "}
                            {p.commission_type === "percentage" ? "%" : "€ HT"}
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2" data-testid={`affiliate-code-svc-commissions-cell-${p.id}`}>
                        {svcComms.length === 0 ? (
                          <span className="text-xs text-zinc-400">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 max-w-[240px]">
                            {svcComms.map((sc, i) => {
                              const isPct = sc.commission_type === "percentage";
                              const suffix = isPct ? "%" : "€ HT";
                              const val = Number(sc.commission_value || 0).toLocaleString("fr-FR", { minimumFractionDigits: isPct ? 0 : 2, maximumFractionDigits: 2 });
                              return (
                                <span
                                  key={`${p.id}-sc-${i}`}
                                  className="inline-flex items-center gap-1 text-[11px] text-zinc-700 border border-zinc-200 rounded-sm px-1.5 py-0.5 bg-zinc-50"
                                  title={`${sc.service_name || sc.service_id}: ${val} ${suffix}`}
                                >
                                  <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: sc.service_color || "#0EA5E9" }} />
                                  <span className="truncate max-w-[110px]">{sc.service_name || sc.service_id}</span>
                                  <span className="font-mono text-emerald-700">{val} {suffix}</span>
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-500 max-w-[220px] truncate" title={p.description || ""}>
                        {p.description || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider ${meta.cls}`} data-testid={`affiliate-code-status-${p.id}`}>
                          {meta.label}
                        </span>
                      </td>
                      {canManage && (
                        <td className="px-3 py-2">
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => toggleAffiliateActive(p)} title={p.manual_active ? "Désactiver" : "Activer"} data-testid={`toggle-affiliate-code-${p.id}`}>
                              <Tag size={12} className={p.manual_active ? "text-emerald-600" : "text-zinc-400"} />
                            </Button>
                            {isAdmin && (
                              <Button variant="ghost" size="sm" onClick={() => openAffiliateEdit(p)} data-testid={`edit-affiliate-code-${p.id}`}>
                                <Pencil size={12} />
                              </Button>
                            )}
                            {isAdmin && (
                              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeAffiliate(p)} data-testid={`delete-affiliate-code-${p.id}`}>
                                <Trash2 size={12} />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {filteredAffiliates.length === 0 && (
                  <tr>
                    <td colSpan={canManage ? 13 : 12} className="px-3 py-8 text-center text-sm text-zinc-500 font-mono">
                      {affiliateList.length === 0
                        ? "Aucun code d'affiliation. Créez votre premier code pour piloter vos partenaires affiliés."
                        : "Aucun code ne correspond à votre recherche."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </HorizontalScrollContainer>
        </TabsContent>

        {/* --------- Codes de parrainage --------- */}
        <TabsContent value="referrals" className="space-y-4">
          {canManage && (
            <div className="flex justify-end">
              <Dialog open={referralOpen} onOpenChange={setReferralOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openReferralCreate} className="bg-[#EC4899] hover:bg-[#DB2777] text-white rounded-sm" data-testid="new-referral-code-btn">
                    <Plus size={14} className="mr-1" /> Nouveau code parrainage
                  </Button>
                </DialogTrigger>
                <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-lg">
                  <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
                    <DialogTitle>{referralEditing ? "Modifier le code parrainage" : "Nouveau code parrainage"}</DialogTitle>
                    <DialogDescription className="text-[11px] text-zinc-500 font-mono">
                      Chaque code est associé à un ou plusieurs départements et à un contact parrain.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1">
                    <div>
                      <Label className="label-uppercase mb-1.5 block">ID du code parrainage</Label>
                      <div className="flex gap-2">
                        <Input
                          value={referralForm.code}
                          onChange={(e) => setReferralForm({ ...referralForm, code: e.target.value.toUpperCase() })}
                          placeholder="ex : PAR-VIP01"
                          className="uppercase tracking-wider"
                          style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}
                          data-testid="referral-code-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={generateReferralCode}
                          disabled={referralGenerating}
                          data-testid="referral-code-generate-btn"
                          title="Générer un code unique"
                        >
                          <RefreshCw size={14} className={referralGenerating ? "animate-spin" : ""} />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Départements associés</Label>
                      <p className="text-[11px] text-zinc-500 font-mono mb-2">Cochez un ou plusieurs départements — le code sera disponible sur chacun d&apos;eux.</p>
                      <div className="space-y-2 border border-zinc-200 rounded-sm p-3 max-h-48 overflow-y-auto" data-testid="referral-code-brand-list">
                        {brands.length === 0 && (
                          <p className="text-xs text-zinc-500 font-mono">Aucun département configuré.</p>
                        )}
                        {brands.map((b) => (
                          <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox
                              checked={referralForm.brand_ids.includes(b.id)}
                              onCheckedChange={() => toggleReferralBrand(b.id)}
                              data-testid={`referral-code-brand-${b.id}`}
                            />
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: b.color }} />
                            <span>{b.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Type de contact</Label>
                      <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setReferralForm({ ...referralForm, contact_type: "beneficiary", collaborator_id: "" })}
                          className={`px-4 py-1.5 text-sm inline-flex items-center gap-1.5 ${referralForm.contact_type === "beneficiary" ? "bg-[#EC4899] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                          data-testid="referral-code-contact-type-beneficiary"
                        >
                          <Contact size={12} /> Bénéficiaire
                        </button>
                        <button
                          type="button"
                          onClick={() => setReferralForm({ ...referralForm, contact_type: "collaborator", beneficiary_id: "" })}
                          className={`px-4 py-1.5 text-sm border-l border-zinc-200 inline-flex items-center gap-1.5 ${referralForm.contact_type === "collaborator" ? "bg-[#EC4899] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                          data-testid="referral-code-contact-type-collaborator"
                        >
                          <Briefcase size={12} /> Collaborateur
                        </button>
                      </div>
                    </div>
                    {referralForm.contact_type === "collaborator" ? (
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Collaborateur</Label>
                        <SearchableSelect
                          value={referralForm.collaborator_id}
                          onChange={(v) => setReferralForm({ ...referralForm, collaborator_id: v })}
                          options={referralCollabs.map((c) => ({
                            value: c.id,
                            label: c.name,
                            keywords: c.email || "",
                            hint: c.email || undefined,
                          }))}
                          placeholder={referralForm.brand_ids.length > 0 ? "Choisir un collaborateur" : "Sélectionnez d'abord un département"}
                          searchPlaceholder="Rechercher un collaborateur…"
                          emptyLabel={referralCollabs.length === 0 ? "Aucun collaborateur pour ces départements" : "Aucun résultat."}
                          disabled={referralForm.brand_ids.length === 0}
                          data-testid="referral-code-collaborator-select"
                        />
                      </div>
                    ) : (
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Contact partenaire</Label>
                        <BeneficiaryCombobox
                          beneficiaries={referralContacts}
                          categoryName="Partenaire"
                          value={referralForm.beneficiary_id}
                          onChange={(v) => setReferralForm({ ...referralForm, beneficiary_id: v })}
                          placeholder={referralForm.brand_ids.length > 0 ? "Choisir un partenaire" : "Sélectionnez d'abord un département"}
                          searchPlaceholder="Rechercher un partenaire…"
                          disabled={referralForm.brand_ids.length === 0}
                          data-testid="referral-code-contact-select"
                        />
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Date d&apos;activation</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full justify-start font-normal"
                              data-testid="referral-code-start-date"
                            >
                              <CalendarDays size={14} className="mr-2 text-zinc-500" />
                              {referralForm.start_date
                                ? formatDate(parseIsoDate(referralForm.start_date), "dd MMM yyyy", { locale: frLocale })
                                : <span className="text-zinc-400">Choisir…</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="p-0 w-auto">
                            <Calendar
                              mode="single"
                              locale={frLocale}
                              selected={referralForm.start_date ? parseIsoDate(referralForm.start_date) : undefined}
                              onSelect={(d) => d && setReferralForm({ ...referralForm, start_date: formatDate(d, "yyyy-MM-dd") })}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Date de fin</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full justify-start font-normal"
                              disabled={referralForm.is_indefinite}
                              data-testid="referral-code-end-date"
                            >
                              <CalendarDays size={14} className="mr-2 text-zinc-500" />
                              {referralForm.is_indefinite
                                ? <span className="text-zinc-400">Indéterminée</span>
                                : (referralForm.end_date
                                    ? formatDate(parseIsoDate(referralForm.end_date), "dd MMM yyyy", { locale: frLocale })
                                    : <span className="text-zinc-400">Choisir…</span>)}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="p-0 w-auto">
                            <Calendar
                              mode="single"
                              locale={frLocale}
                              selected={referralForm.end_date ? parseIsoDate(referralForm.end_date) : undefined}
                              onSelect={(d) => {
                                if (!d) return;
                                const iso = formatDate(d, "yyyy-MM-dd");
                                setReferralForm((f) => {
                                  const next = { ...f, end_date: iso };
                                  if (f.start_date && iso < f.start_date) next.start_date = iso;
                                  return next;
                                });
                              }}
                              defaultMonth={referralForm.end_date ? parseIsoDate(referralForm.end_date) : (referralForm.start_date ? parseIsoDate(referralForm.start_date) : undefined)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={referralForm.is_indefinite}
                        onCheckedChange={(v) => setReferralForm({ ...referralForm, is_indefinite: !!v, end_date: v ? "" : referralForm.end_date })}
                        data-testid="referral-code-indefinite"
                      />
                      <span>Durée <strong>indéterminée</strong> (sans date de fin)</span>
                    </label>
                    <div className="rounded-sm border border-[#EC4899]/30 bg-[#EC4899]/5 px-3 py-2 flex items-center justify-between" data-testid="referral-code-duration-preview">
                      <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1.5">
                        <CalendarDays size={12} /> Durée de validité
                      </span>
                      <span className="text-sm font-semibold text-zinc-900 font-mono">
                        {computeReferralDuration()}
                      </span>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Type de remise</Label>
                      <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setReferralForm({ ...referralForm, discount_type: "percentage" })}
                          className={`px-4 py-1.5 text-sm ${referralForm.discount_type === "percentage" ? "bg-[#EC4899] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                          data-testid="referral-code-discount-percentage"
                        >
                          Pourcentage (%)
                        </button>
                        <button
                          type="button"
                          onClick={() => setReferralForm({ ...referralForm, discount_type: "amount" })}
                          className={`px-4 py-1.5 text-sm border-l border-zinc-200 ${referralForm.discount_type === "amount" ? "bg-[#EC4899] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                          data-testid="referral-code-discount-amount"
                        >
                          Montant fixe (€)
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Valeur de la remise</Label>
                      <div className="relative">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={referralForm.discount_value}
                          onChange={(e) => setReferralForm({ ...referralForm, discount_value: e.target.value })}
                          data-testid="referral-code-discount-value"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-zinc-500 pointer-events-none">
                          {referralForm.discount_type === "percentage" ? "%" : "€"}
                        </span>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={referralForm.manual_active}
                        onCheckedChange={(v) => setReferralForm({ ...referralForm, manual_active: !!v })}
                        data-testid="referral-code-manual-active"
                      />
                      <span>Code <strong>actif</strong> (décochez pour désactiver manuellement)</span>
                    </label>
                    <div className="border-t border-zinc-100 pt-4">
                      <OffersEditor
                        value={referralForm.offers}
                        onChange={(next) => setReferralForm({ ...referralForm, offers: next })}
                        services={referralServices}
                        testid="referral-offer"
                      />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Description</Label>
                      <Textarea
                        value={referralForm.description}
                        onChange={(e) => setReferralForm({ ...referralForm, description: e.target.value })}
                        rows={2}
                        placeholder="Contexte, conditions, campagne de parrainage… (facultatif)"
                        data-testid="referral-code-description"
                      />
                    </div>
                  </div>
                  <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
                    <Button variant="outline" onClick={() => setReferralOpen(false)}>Annuler</Button>
                    <Button onClick={saveReferral} className="bg-[#EC4899] hover:bg-[#DB2777] text-white" data-testid="referral-code-save-btn">Enregistrer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <QuickSearchBar
            value={referralFilters}
            onChange={setReferralFilters}
            placeholder="Rechercher un code, un contact, une description…"
            testid="referrals-cats-search"
            filters={[
              { key: "status", label: "Statut", type: "select", placeholder: "Tous", options: [
                { value: "active", label: "Actifs" },
                { value: "inactive", label: "Inactifs" },
              ] },
              { key: "brand_id", label: "Département", type: "select", placeholder: "Tous les départements", options: brands.map((b) => ({ value: b.id, label: b.name })) },
            ]}
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-zinc-500 font-mono" data-testid="referrals-cats-result-count">
              {filteredReferrals.length} résultat{filteredReferrals.length > 1 ? "s" : ""} sur {referralList.length}
            </p>
            <ExportCsvButton
              filename="codes_parrainage"
              testid="referrals-cats-export-csv"
              rows={filteredReferrals}
              columns={[
                { label: "Code", value: (p) => p.code },
                { label: "Départements", value: (p) => (p.brand_names && p.brand_names.length ? p.brand_names.join(", ") : (p.brand_name || "")) },
                { label: "Type de contact", value: (p) => p.contact_type === "collaborator" ? "Collaborateur" : "Bénéficiaire" },
                { label: "Contact parrain", value: (p) => p.contact_name || "" },
                { label: "Date activation", value: (p) => formatDateFR(p.start_date) },
                { label: "Date fin", value: (p) => p.is_indefinite ? "Indéterminée" : formatDateFR(p.end_date) },
                { label: "Durée (jours)", value: (p) => p.duration_days ?? "Indéterminée" },
                { label: "Remise", value: (p) => `${p.discount_value}${p.discount_type === "percentage" ? "%" : "€"}` },
                { label: "Offres", value: (p) => offersToCsv(p.offers) },
                { label: "Statut", value: (p) => p.is_active ? "Actif" : (p.status || "Inactif") },
              ]}
            />
          </div>
          <HorizontalScrollContainer testId="referral-codes-table" className="bg-white border border-zinc-200 rounded-sm">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                <tr>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Départements</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Contact parrain</th>
                  <th className="text-left px-3 py-2">Activation</th>
                  <th className="text-left px-3 py-2">Fin</th>
                  <th className="text-right px-3 py-2">Durée</th>
                  <th className="text-right px-3 py-2">Remise</th>
                  <th className="text-left px-3 py-2">Offres</th>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-left px-3 py-2">Statut</th>
                  {canManage && <th className="px-3 py-2 w-28"></th>}
                </tr>
              </thead>
              <tbody>
                {filteredReferrals.map((p) => {
                  const meta = referralStatusMeta(p);
                  const discountLabel = `${Number(p.discount_value || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} ${p.discount_type === "percentage" ? "%" : "€"}`;
                  const isCollab = p.contact_type === "collaborator";
                  const brandChips = (p.brand_names && p.brand_names.length
                    ? p.brand_names.map((name, i) => ({ name, color: (p.brand_colors && p.brand_colors[i]) || "#EC4899" }))
                    : (p.brand_name ? [{ name: p.brand_name, color: p.brand_color || "#EC4899" }] : []));
                  return (
                    <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`referral-code-${p.id}`}>
                      <td className="px-3 py-2">
                        <div className="inline-flex items-center gap-1.5">
                          <Users size={12} className="text-[#EC4899] flex-shrink-0" />
                          <span className="font-mono text-xs font-semibold text-zinc-900 tracking-wider">{p.code}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {brandChips.length === 0 ? (
                          <span className="text-xs text-zinc-500">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {brandChips.map((bc, i) => (
                              <span key={`${p.id}-b-${i}`} className="inline-flex items-center gap-1.5 text-[11px] text-zinc-700 border border-zinc-200 rounded-sm px-1.5 py-0.5 bg-zinc-50">
                                <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: bc.color }} />
                                {bc.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider ${isCollab ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                          {isCollab ? "Collab." : "Bénéf."}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-700">
                        <span className="inline-flex items-center gap-1">
                          {isCollab ? <Briefcase size={11} className="text-zinc-400" /> : <Contact size={11} className="text-zinc-400" />}
                          {p.contact_name || p.beneficiary_name || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-600">{formatDateFR(p.start_date) || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-600">
                        {p.is_indefinite ? <span className="italic text-zinc-500">Indéterminée</span> : (formatDateFR(p.end_date) || "—")}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[11px] text-zinc-500">
                        {p.duration_days != null ? `${p.duration_days} j` : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="font-mono font-semibold text-[#EC4899]">{discountLabel}</span>
                      </td>
                      <td className="px-3 py-2" data-testid={`referral-code-offers-cell-${p.id}`}>
                        <OffersCell offers={p.offers} accent="#EC4899" />
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-500 max-w-[220px] truncate" title={p.description || ""}>
                        {p.description || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider ${meta.cls}`} data-testid={`referral-code-status-${p.id}`}>
                          {meta.label}
                        </span>
                      </td>
                      {canManage && (
                        <td className="px-3 py-2">
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => toggleReferralActive(p)} title={p.manual_active ? "Désactiver" : "Activer"} data-testid={`toggle-referral-code-${p.id}`}>
                              <Tag size={12} className={p.manual_active ? "text-emerald-600" : "text-zinc-400"} />
                            </Button>
                            {isAdmin && (
                              <Button variant="ghost" size="sm" onClick={() => openReferralEdit(p)} data-testid={`edit-referral-code-${p.id}`}>
                                <Pencil size={12} />
                              </Button>
                            )}
                            {isAdmin && (
                              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeReferral(p)} data-testid={`delete-referral-code-${p.id}`}>
                                <Trash2 size={12} />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {filteredReferrals.length === 0 && (
                  <tr>
                    <td colSpan={canManage ? 12 : 11} className="px-3 py-8 text-center text-sm text-zinc-500 font-mono">
                      {referralList.length === 0
                        ? "Aucun code parrainage. Créez votre premier code pour animer votre programme de parrainage."
                        : "Aucun code ne correspond à votre recherche."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </HorizontalScrollContainer>
        </TabsContent>
      </Tabs>
    </div>
  );
}
